import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaPaperPlane, FaTrash, FaEnvelope, FaFilePdf,
  FaGlobe, FaSignOutAlt, FaShieldAlt, FaCheck,
  FaTimes, FaBold, FaItalic, FaUnderline,
  FaMicrophone, FaMicrophoneSlash,
  FaDownload
} from "react-icons/fa";

// ── Google Icon ────────────────────────────────────────────────────
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

// ── Santharion AI Logo — simple star that renders everywhere ──────
function SantharionLogo({ size = 32 }) {
  const s = size;
  const c = s / 2;
  const outerR = s * 0.44;
  const innerR = s * 0.20;
  const points = 5;
  const pts = [];
  for (let i = 0; i < points * 2; i++) {
    const r   = i % 2 === 0 ? outerR : innerR;
    const ang = (Math.PI / points) * i - Math.PI / 2;
    pts.push(`${(c + r * Math.cos(ang)).toFixed(2)},${(c + r * Math.sin(ang)).toFixed(2)}`);
  }
  return (
    <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`} fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={`sg-${s}`} x1="0" y1="0" x2={s} y2={s} gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="#f97316"/>
          <stop offset="50%"  stopColor="#ec4899"/>
          <stop offset="100%" stopColor="#8b5cf6"/>
        </linearGradient>
      </defs>
      <circle cx={c} cy={c} r={c - 1} fill="none" stroke={`url(#sg-${s})`} strokeWidth="1.5" opacity="0.6"/>
      <polygon points={pts.join(" ")} fill={`url(#sg-${s})`}/>
      <circle cx={c} cy={c} r={s * 0.08} fill="white" opacity="0.95"/>
    </svg>
  );
}

// ── Avatar emoji picker based on name ─────────────────────────────
function getAvatarEmoji(name) {
  if (!name) return "😊";
  const emojis = ["😊","🦁","🐯","🦊","🐺","🦝","🐻","🐼","🐨","🦄",
                  "🐸","🦋","🦅","🦉","🐬","🦈","🐙","🦁","🌟","⚡",
                  "🔥","💫","🌈","🎯","🚀","💎","🎸","🎭","🏆","👑"];
  const idx = (name.charCodeAt(0) || 0) % emojis.length;
  return emojis[idx];
}

// AI Cartoon Avatar — unique per user based on name
function AiAvatar({ name, size = 40 }) {
  // Generate consistent colors from name
  const hash = (name || "U").split("").reduce((a,c) => a + c.charCodeAt(0), 0);
  const colors = [
    ["#f97316","#ec4899"], ["#8b5cf6","#3b82f6"], ["#10b981","#06b6d4"],
    ["#f59e0b","#ef4444"], ["#6366f1","#ec4899"], ["#14b8a6","#8b5cf6"],
  ];
  const [c1, c2] = colors[hash % colors.length];

  // Face features vary by name
  const _faceStyle = hash % 4; // eslint-disable-line no-unused-vars
  const _initials = (name || "U").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase(); // eslint-disable-line no-unused-vars

  // Eye shapes
  const eyes = [
    <><circle cx="13" cy="17" r="2.5" fill="white"/><circle cx="27" cy="17" r="2.5" fill="white"/>
      <circle cx="13.8" cy="17.5" r="1.2" fill="#1a0a2e"/><circle cx="27.8" cy="17.5" r="1.2" fill="#1a0a2e"/></>,
    <><ellipse cx="13" cy="17" rx="2.5" ry="2" fill="white"/><ellipse cx="27" cy="17" rx="2.5" ry="2" fill="white"/>
      <circle cx="13.5" cy="17.2" r="1.2" fill="#1a0a2e"/><circle cx="27.5" cy="17.2" r="1.2" fill="#1a0a2e"/></>,
    <><rect x="10.5" y="15" width="5" height="4" rx="2" fill="white"/><rect x="24.5" y="15" width="5" height="4" rx="2" fill="white"/>
      <circle cx="13" cy="17" r="1.2" fill="#1a0a2e"/><circle cx="27" cy="17" r="1.2" fill="#1a0a2e"/></>,
    <><circle cx="13" cy="17" r="2.5" fill="white"/><circle cx="27" cy="17" r="2.5" fill="white"/>
      <circle cx="14" cy="16.5" r="1.4" fill="#1a0a2e"/><circle cx="28" cy="16.5" r="1.4" fill="#1a0a2e"/></>,
  ];

  // Mouth styles
  const mouths = [
    <path d="M14 24 Q20 28 26 24" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round"/>,
    <path d="M14 24 Q20 27 26 24" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round"/>,
    <path d="M15 23 Q20 27 25 23 Q20 29 15 23" fill="white" opacity="0.9"/>,
    <ellipse cx="20" cy="24.5" rx="4" ry="2.5" fill="white" opacity="0.85"/>,
  ];

  // Hair/top decoration
  const hairs = [
    <path d="M8 18 Q10 6 20 5 Q30 6 32 18 Q28 8 20 8 Q12 8 8 18Z" fill={c1} opacity="0.9"/>,
    <path d="M8 16 Q12 4 20 4 Q28 4 32 16 Q30 6 20 6 Q10 6 8 16Z" fill={c1}/>,
    <><path d="M8 18 Q10 5 20 5 Q30 5 32 18 Q28 7 20 7 Q12 7 8 18Z" fill={c1}/>
      <circle cx="15" cy="6" r="3" fill={c2}/><circle cx="25" cy="6" r="3" fill={c2}/></>,
    <path d="M6 18 Q8 3 20 3 Q32 3 34 18 Q28 5 20 5 Q12 5 6 18Z" fill={c1}/>,
  ];

  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Background */}
      <defs>
        <linearGradient id={`bg-${hash}`} x1="0" y1="0" x2="40" y2="40">
          <stop offset="0%" stopColor={c1}/>
          <stop offset="100%" stopColor={c2}/>
        </linearGradient>
      </defs>
      <circle cx="20" cy="20" r="20" fill={`url(#bg-${hash})`}/>
      {/* Face */}
      <ellipse cx="20" cy="22" rx="11" ry="12" fill="#fde8d0"/>
      {/* Hair */}
      {hairs[hash % 4]}
      {/* Eyes */}
      {eyes[hash % 4]}
      {/* Mouth */}
      {mouths[hash % 4]}
      {/* Blush */}
      <circle cx="12" cy="22" r="2.5" fill="#f97316" opacity="0.3"/>
      <circle cx="28" cy="22" r="2.5" fill="#f97316" opacity="0.3"/>
    </svg>
  );
}

// ── Typewriter ─────────────────────────────────────────────────────
function Typewriter({ text, onDone }) {
  const [displayed, setDisplayed] = useState("");
  const [done, setDone] = useState(false);
  const idx = useRef(0);
  useEffect(() => {
    idx.current = 0; setDisplayed(""); setDone(false);
    const iv = setInterval(() => {
      if (idx.current < text.length) setDisplayed(text.slice(0, ++idx.current));
      else { clearInterval(iv); setDone(true); onDone?.(); }
    }, 6);
    return () => clearInterval(iv);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <span>
      {renderFormatted(displayed)}
      {!done && <span className="inline-block w-0.5 h-4 ml-0.5 animate-pulse align-middle" style={{ background: "#f97316" }} />}
    </span>
  );
}

function renderFormatted(text) {
  if (!text) return null;
  return text.split("\n").map((line, i, arr) => (
    <span key={i}>
      {line.split(/(\*\*.*?\*\*)/).map((part, j) =>
        part.startsWith("**") && part.endsWith("**")
          ? <strong key={j} style={{ color: "#fb923c" }} className="font-semibold">{part.slice(2, -2)}</strong>
          : part
      )}
      {i < arr.length - 1 && <br />}
    </span>
  ));
}

// ═══════════════════════════════════════════════════════════════════
// VOICE INPUT HOOK — lazy init to avoid SSR/strict mode issues
// ═══════════════════════════════════════════════════════════════════
function useVoiceInput(onResult) {
  const [listening,  setListening]  = useState(false);
  const [supported,  setSupported]  = useState(true); // optimistic — check on first use
  const recognitionRef = useRef(null);

  const getOrCreateRecognition = () => {
    if (recognitionRef.current) return recognitionRef.current;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setSupported(false);
      return null;
    }
    const rec = new SpeechRecognition();
    rec.continuous     = false;
    rec.interimResults = false;
    rec.lang           = "en-US";
    rec.onresult = (e) => { onResult(e.results[0][0].transcript); setListening(false); };
    rec.onerror  = ()  => setListening(false);
    rec.onend    = ()  => setListening(false);
    recognitionRef.current = rec;
    return rec;
  };

  const toggle = () => {
    const rec = getOrCreateRecognition();
    if (!rec) { alert("Voice input is not supported in this browser. Please use Chrome or Edge."); return; }
    if (listening) { rec.stop(); setListening(false); }
    else           { try { rec.start(); setListening(true); } catch(e) { setListening(false); } }
  };

  return { listening, supported, toggle };
}

// ═══════════════════════════════════════════════════════════════════
// GMAIL COMPOSE MODAL
// ═══════════════════════════════════════════════════════════════════
function GmailComposeModal({ template, senderName, senderEmail, senderAccessToken, onSend, onClose, onSessionExpired, onSchedule }) {
  const [to,           setTo]           = useState(template.to      || "");
  const [subject,      setSubject]      = useState(template.subject || "");
  const [body,         setBody]         = useState(template.body    || "");
  const [sending,      setSending]      = useState(false);
  const [sent,         setSent]         = useState(false);
  const [files,        setFiles]        = useState([]);
  const [dragOver,     setDragOver]     = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [schedDate,    setSchedDate]    = useState("");
  const [schedTime,    setSchedTime]    = useState("");
  const [scheduled,    setScheduled]    = useState(false);
  const [schedError,   setSchedError]   = useState(false); // red shake when send clicked without date/time
  const fileInputRef = useRef(null);

  // Compute smart suggested times
  const suggestedTimes = (() => {
    const now = new Date();
    const pad = n => String(n).padStart(2,"0");
    const fmt = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}|${pad(d.getHours())}:${pad(d.getMinutes())}`;
    const slots = [];
    // Tomorrow 9 AM
    const t1 = new Date(now); t1.setDate(t1.getDate()+1); t1.setHours(9,0,0,0);
    slots.push({ label:"Tomorrow 9 AM", ...(() => { const [d,t]=fmt(t1).split("|"); return{date:d,time:t}; })() });
    // Tomorrow 2 PM
    const t2 = new Date(t1); t2.setHours(14,0,0,0);
    slots.push({ label:"Tomorrow 2 PM", ...(() => { const [d,t]=fmt(t2).split("|"); return{date:d,time:t}; })() });
    // Monday 9 AM (next weekday)
    const t3 = new Date(now); t3.setDate(t3.getDate() + ((1 + 7 - t3.getDay()) % 7 || 7)); t3.setHours(9,0,0,0);
    slots.push({ label:"Monday 9 AM", ...(() => { const [d,t]=fmt(t3).split("|"); return{date:d,time:t}; })() });
    return slots;
  })();

  const handleScheduleSend = async () => {
    if (!schedDate || !schedTime) { alert("Please pick a date and time."); return; }
    const sendAtMs = new Date(`${schedDate}T${schedTime}`).getTime();
    if (sendAtMs <= Date.now()) { alert("Please pick a future date and time."); return; }
    setSending(true);
    try {
      // Save to backend — Render server will send it even if tab is closed
      const res = await fetch("/api/email/schedule", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: to.trim(), subject, body,
          sendAt: new Date(sendAtMs).toISOString(),
          accessToken: senderAccessToken,
          fromEmail: senderEmail,
          fromName: senderName,
        })
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Schedule failed");

      // Also update local UI state
      if (onSchedule) {
        onSchedule({
          id: data.data?.id || Date.now().toString(),
          to: to.trim(), subject, body,
          sendAt: sendAtMs,
          accessToken: senderAccessToken,
          fromEmail: senderEmail,
          fromName: senderName,
          sent: false,
        });
      }
      setScheduled(true);
      setTimeout(() => onClose(), 2000);
    } catch(e) {
      alert("Failed to schedule: " + e.message);
    } finally {
      setSending(false);
    }
  };

  const handleSend = async () => {
    const cleanTo = to.trim();
    if (!cleanTo) { alert("Please enter a recipient email address."); return; }
    if (!cleanTo.includes("@")) { alert(`"${cleanTo}" is not a valid email.`); return; }
    setSending(true);
    try {
      if (files.length > 0) {
        // Use attachment endpoint
        const formData = new FormData();
        formData.append("to",          cleanTo);
        formData.append("subject",     subject);
        formData.append("body",        body);
        formData.append("accessToken", senderAccessToken || "");
        formData.append("fromEmail",   senderEmail || "");
        formData.append("fromName",    senderName  || "");
        files.forEach(f => formData.append("files", f));
        const res  = await fetch("/api/email/send-with-attachment", { method:"POST", body:formData });
        const data = await res.json();
        if (!data.success) throw new Error(data.message || "Send failed");
        // Notify parent so it can show success message and track thread
        if (onSend) await onSend(cleanTo, subject, body, null, null, data.data?.threadId);
      } else {
        console.log("[Send] No files — calling onSend. senderAccessToken empty:", !senderAccessToken);
        await onSend(cleanTo, subject, body, null, null, null);
      }
      setSent(true);
      setTimeout(() => { onClose(); }, 1500);
    } catch (e) {
      setSending(false);
      if (e.message && e.message.toLowerCase().includes("session expired")) {
        onClose();
        if (onSessionExpired) onSessionExpired();
      } else {
        alert("Failed: " + e.message);
      }
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files);
    setFiles(prev => [...prev, ...dropped].slice(0, 5));
  };

  const removeFile = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

  const formatSize = (bytes) => bytes < 1024*1024
    ? (bytes/1024).toFixed(1)+"KB"
    : (bytes/1024/1024).toFixed(1)+"MB";

  const isMobileView = typeof window !== "undefined" && window.innerWidth < 640;

  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      style={{ position:"fixed", inset:0, zIndex:9999, background:"rgba(0,0,0,0.75)",
        backdropFilter:"blur(8px)", display:"flex",
        flexDirection:"column",
        alignItems:"center",
        justifyContent: isMobileView ? "flex-end" : "center",
        padding: isMobileView ? "0" : "20px" }}
      onClick={e => e.target === e.currentTarget && onClose()}>

      <motion.div
        initial={{ opacity:0, y: isMobileView ? 80 : 0, scale: isMobileView ? 1 : 0.95 }}
        animate={{ opacity:1, y:0, scale:1 }}
        exit={{ opacity:0, y: isMobileView ? 80 : 0, scale: isMobileView ? 1 : 0.95 }}
        transition={{ type:"spring", stiffness:320, damping:32 }}
        style={{
          width:"100%", maxWidth:580,
          borderRadius: isMobileView ? "22px 22px 0 0" : 20,
          display:"flex", flexDirection:"column",
          background:"#ffffff",
          boxShadow:"0 -8px 60px rgba(0,0,0,0.5)",
          /* 
            Mobile: use actual window.innerHeight so it works on ALL devices.
            130px = approx input bar + browser bottom bar.
            The modal always leaves room for Send button — it's flex column,
            scrollable middle, fixed footer at bottom.
          */
          height: isMobileView ? (window.innerHeight - 200) + "px" : "auto",
          maxHeight: isMobileView ? (window.innerHeight - 200) + "px" : "82vh",
          marginBottom: isMobileView ? 130 : 0,
          overflow:"hidden",
        }}>

        {/* ── DRAG HANDLE — mobile only ── */}
        {isMobileView && (
          <div style={{ flexShrink:0, paddingTop:10, paddingBottom:4,
            background:"linear-gradient(135deg,#1e1b2e,#2d1b4e)", textAlign:"center" }}>
            <div style={{ width:36, height:4, borderRadius:999,
              background:"rgba(255,255,255,0.3)", display:"inline-block" }}/>
          </div>
        )}

        {/* ── HEADER ── */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
          padding: isMobileView ? "10px 16px 12px" : "14px 16px", flexShrink:0,
          background:"linear-gradient(135deg,#1e1b2e,#2d1b4e)" }}>
          <span style={{ color:"#fff", fontSize:14, fontWeight:700, display:"flex", alignItems:"center", gap:8 }}>
            <FaEnvelope size={13} style={{ color:"#f97316" }}/>
            {"✉️ New Email"}
          </span>
          <button onClick={onClose}
            style={{ width:32, height:32, borderRadius:8, background:"rgba(255,255,255,0.1)",
              border:"none", color:"#9ca3af", cursor:"pointer", fontSize:18,
              display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
        </div>

        {/* ── TO ── always visible, above scroll */}
        <div style={{ display:"flex", alignItems:"center", padding:"11px 16px",
          borderBottom:"1px solid #f0f0f0", flexShrink:0, background:"#fff" }}>
          <span style={{ fontSize:13, color:"#9ca3af", width:54, flexShrink:0, fontWeight:600 }}>To</span>
          <input value={to} onChange={e => setTo(e.target.value)}
            placeholder="recipient@email.com"
            style={{ flex:1, fontSize:15, color:"#111827", outline:"none",
              background:"transparent", border:"none", fontFamily:"inherit" }}/>
        </div>

        {/* ── SUBJECT ── always visible, above scroll */}
        <div style={{ display:"flex", alignItems:"center", padding:"11px 16px",
          borderBottom:"1.5px solid #e5e7eb", flexShrink:0, background:"#fff" }}>
          <span style={{ fontSize:13, color:"#9ca3af", width:54, flexShrink:0, fontWeight:600 }}>Subject</span>
          <input value={subject} onChange={e => setSubject(e.target.value)}
            placeholder="Email subject"
            style={{ flex:1, fontSize:15, color:"#111827", outline:"none",
              background:"transparent", border:"none", fontFamily:"inherit", fontWeight:600 }}/>
        </div>

        {/* ── SCROLLABLE MIDDLE — body + extras ── */}
        <div style={{ flex:1, overflowY:"auto", minHeight:0,
          WebkitOverflowScrolling:"touch", background:"#fff" }}>

          {/* Body */}
          <textarea value={body} onChange={e => setBody(e.target.value)}
            placeholder="Write your email here..."
            style={{ width:"100%", padding:"12px 16px", fontSize:15, color:"#1f2937",
              outline:"none", resize:"none", border:"none", fontFamily:"inherit",
              lineHeight:1.7, boxSizing:"border-box",
              minHeight: isMobileView ? 90 : 130,
              display:"block" }}/>

          {/* From label */}
          <div style={{ padding:"6px 16px", borderTop:"1px solid #f5f5f5", background:"#fafafa" }}>
            <p style={{ fontSize:11, color:"#9ca3af", margin:0,
              overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              Sending as <span style={{ color:"#374151", fontWeight:600 }}>{senderName} &lt;{senderEmail}&gt;</span>
            </p>
          </div>

          {/* Attachments */}
          <div onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)} onDrop={handleFileDrop}
            style={{ padding:"8px 12px" }}>
            {files.length > 0 && (
              <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:8 }}>
                {files.map((f, i) => (
                  <div key={i} style={{ display:"flex", alignItems:"center", gap:5, padding:"4px 10px",
                    borderRadius:8, fontSize:12, background:"rgba(249,115,22,0.08)",
                    border:"1px solid rgba(249,115,22,0.25)" }}>
                    <span>{f.type?.startsWith("image/") ? "🖼️" : f.type?.includes("pdf") ? "📄" : "📎"}</span>
                    <span style={{ color:"#fb923c", fontWeight:600, maxWidth:90,
                      overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{f.name}</span>
                    <span style={{ color:"#9ca3af" }}>({formatSize(f.size)})</span>
                    <button onClick={() => removeFile(i)}
                      style={{ color:"#9ca3af", background:"none", border:"none",
                        cursor:"pointer", fontSize:13, padding:0, lineHeight:1 }}>✕</button>
                  </div>
                ))}
              </div>
            )}
            <div onClick={() => fileInputRef.current?.click()}
              style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 12px",
                borderRadius:12, cursor:"pointer",
                border: dragOver ? "2px dashed #f97316" : "2px dashed #e5e7eb",
                background: dragOver ? "rgba(249,115,22,0.04)" : "transparent" }}>
              <span style={{ fontSize:16 }}>📎</span>
              <span style={{ fontSize:12, color: dragOver ? "#fb923c" : "#9ca3af" }}>
                {dragOver ? "Drop files here!" : isMobileView ? "Tap to attach" : "Attach files — drag & drop or click"}
              </span>
              <input ref={fileInputRef} type="file" multiple style={{ display:"none" }}
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
                onChange={e => setFiles(prev => [...prev, ...Array.from(e.target.files)].slice(0,5))}/>
            </div>
            {files.length >= 5 && (
              <p style={{ fontSize:11, color:"#f59e0b", margin:"4px 0 0" }}>Max 5 files</p>
            )}
          </div>

          {/* ── SCHEDULE PANEL — inside scroll ── */}
          {showSchedule && (
            <div style={{ padding:"14px 16px", borderTop:"2px solid rgba(124,58,237,0.15)",
              background:"#0f0720" }}>
              {scheduled ? (
                <div style={{ textAlign:"center", padding:"10px 0" }}>
                  <div style={{ fontSize:28, marginBottom:6 }}>✅</div>
                  <div style={{ fontSize:14, fontWeight:800, color:"#a78bfa" }}>Email Scheduled!</div>
                  <div style={{ fontSize:12, color:"#6b7280", marginTop:4 }}>
                    Sends on {new Date(`${schedDate}T${schedTime}`).toLocaleString("en-IN",{dateStyle:"medium",timeStyle:"short"})}
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ fontSize:13, fontWeight:700, color:"#c4b5fd", marginBottom:10,
                    display:"flex", alignItems:"center", gap:6 }}>
                    📅 Schedule this email
                  </div>
                  <div style={{ display:"flex", gap:8, marginBottom:12, flexWrap:"wrap" }}>
                    {suggestedTimes.map((s,i) => {
                      const isSelected = schedDate===s.date && schedTime===s.time;
                      return (
                        <button key={i} onClick={() => { setSchedDate(s.date); setSchedTime(s.time); }}
                          style={{ padding:"7px 12px", borderRadius:999, fontSize:12, fontWeight:700,
                            cursor:"pointer", fontFamily:"inherit",
                            background: isSelected ? "linear-gradient(135deg,#7c3aed,#a855f7)" : "rgba(124,58,237,0.15)",
                            color: isSelected ? "#fff" : "#c4b5fd",
                            border: isSelected ? "1.5px solid #7c3aed" : "1px solid rgba(124,58,237,0.3)" }}>
                          ⚡ {s.label}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ display:"flex", flexDirection: isMobileView ? "column" : "row", gap:8, marginBottom:12 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:11, color:"#a78bfa", fontWeight:700, marginBottom:5 }}>📅 Date</div>
                      <input type="date" value={schedDate} onChange={e => { setSchedDate(e.target.value); setSchedError(false); }}
                        style={{ width:"100%", padding:"11px 12px", borderRadius:10,
                          border: schedError && !schedDate ? "2px solid #ef4444"
                            : schedDate ? "2px solid #7c3aed" : "1.5px solid rgba(124,58,237,0.3)",
                          fontSize:14, outline:"none", background: schedError && !schedDate ? "rgba(239,68,68,0.08)" : "#1a0a35",
                          color:"#e2e8f0", fontFamily:"inherit", colorScheme:"dark",
                          boxSizing:"border-box", WebkitAppearance:"none",
                          animation: schedError && !schedDate ? "schedShake 0.4s ease" : "none" }}/>
                      {schedError && !schedDate && (
                        <div style={{ fontSize:11, color:"#ef4444", fontWeight:700, marginTop:4, display:"flex", alignItems:"center", gap:4 }}>
                          ⚠️ Please select a date
                        </div>
                      )}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:11, color:"#a78bfa", fontWeight:700, marginBottom:5 }}>🕐 Time</div>
                      <input type="time" value={schedTime} onChange={e => { setSchedTime(e.target.value); setSchedError(false); }}
                        style={{ width:"100%", padding:"11px 12px", borderRadius:10,
                          border: schedError && !schedTime ? "2px solid #ef4444"
                            : schedTime ? "2px solid #7c3aed" : "1.5px solid rgba(124,58,237,0.3)",
                          fontSize:14, outline:"none", background: schedError && !schedTime ? "rgba(239,68,68,0.08)" : "#1a0a35",
                          color:"#e2e8f0", fontFamily:"inherit", colorScheme:"dark",
                          boxSizing:"border-box", WebkitAppearance:"none",
                          animation: schedError && !schedTime ? "schedShake 0.4s ease" : "none" }}/>
                      {schedError && !schedTime && (
                        <div style={{ fontSize:11, color:"#ef4444", fontWeight:700, marginTop:4, display:"flex", alignItems:"center", gap:4 }}>
                          ⚠️ Please select a time
                        </div>
                      )}
                    </div>
                  </div>
                  {schedDate && schedTime && (
                    <div style={{ padding:"8px 12px", borderRadius:10, background:"rgba(124,58,237,0.1)",
                      border:"1px solid rgba(124,58,237,0.25)", marginBottom:10, fontSize:12, color:"#c4b5fd" }}>
                      📬 Will send: {new Date(`${schedDate}T${schedTime}`).toLocaleString("en-IN",{dateStyle:"full",timeStyle:"short"})}
                    </div>
                  )}
                  {/* Hint — the main Send button at bottom handles scheduling */}
                  {schedDate && schedTime ? (
                    <div style={{ padding:"10px 14px", borderRadius:12,
                      background:"rgba(124,58,237,0.15)", border:"1px solid rgba(124,58,237,0.35)",
                      display:"flex", alignItems:"center", gap:10 }}>
                      <span style={{ fontSize:18 }}>✅</span>
                      <div>
                        <div style={{ fontSize:13, fontWeight:800, color:"#c4b5fd" }}>Ready to schedule!</div>
                        <div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>Tap the purple Send button below ↓</div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ padding:"10px 14px", borderRadius:12,
                      background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)",
                      fontSize:12, color:"#6b7280" }}>
                      👆 Pick a date &amp; time — the Send button will turn purple and schedule it for you
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* ══════════════════════════════════════════════
             SMART SEND FOOTER — always pinned at bottom
             If date+time selected → Schedule  else → Send Now
        ══════════════════════════════════════════════ */}
        {(() => {
          const isScheduleMode = showSchedule && schedDate && schedTime;
          const canSend = !sending && !sent && to && subject;
          return (
            <div style={{ flexShrink:0, background:"#fff",
              borderTop: isScheduleMode ? "2px solid rgba(124,58,237,0.25)" : "2px solid #f0f0f0",
              boxShadow:"0 -6px 20px rgba(0,0,0,0.07)" }}>

              {/* Schedule toggle row — only shown when 📅 is open and no date selected yet */}
              {showSchedule && (!schedDate || !schedTime) && !sent && !sending && (
                <div style={{ padding:"8px 14px 0",
                  display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:12, flex:1,
                    color: schedError ? "#ef4444" : "#9ca3af",
                    fontWeight: schedError ? 700 : 400 }}>
                    {schedError ? "⛔ Select date & time before sending!" : "☝️ Pick a date & time above, then tap Send to schedule"}
                  </span>
                  <button onClick={() => setShowSchedule(false)}
                    style={{ fontSize:11, color:"#9ca3af", background:"none", border:"none",
                      cursor:"pointer", fontFamily:"inherit", padding:"4px 8px",
                      borderRadius:8, background:"rgba(0,0,0,0.04)" }}>
                    Cancel
                  </button>
                </div>
              )}

              <div style={{ padding: isMobileView ? "10px 14px 18px" : "12px 16px",
                display:"flex", gap:10, alignItems:"center" }}>

                {/* Main smart send button */}
                <motion.button
                  whileTap={{ scale: canSend ? 0.97 : 1 }}
                  onClick={() => {
                    // If 📅 is open but user hasn't picked date+time — shake and warn
                    if (showSchedule && (!schedDate || !schedTime)) {
                      setSchedError(true);
                      setTimeout(() => setSchedError(false), 1500);
                      return;
                    }
                    if (isScheduleMode) handleScheduleSend();
                    else handleSend();
                  }}
                  disabled={!canSend}
                  style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                    padding: isMobileView ? "14px 20px" : "13px 20px",
                    borderRadius:14, border:"none",
                    cursor: !canSend ? "not-allowed" : "pointer",
                    background: sent ? "#22c55e"
                      : !canSend ? "#e5e7eb"
                      : isScheduleMode ? "linear-gradient(135deg,#7c3aed,#a855f7)"
                      : "linear-gradient(135deg,#f97316,#ec4899)",
                    color: !canSend ? "#9ca3af" : "#fff",
                    fontSize: isMobileView ? 16 : 15, fontWeight:900, fontFamily:"inherit",
                    boxShadow: !canSend || sent ? "none"
                      : isScheduleMode ? "0 4px 20px rgba(124,58,237,0.45)"
                      : "0 4px 20px rgba(249,115,22,0.4)",
                    opacity: sending ? 0.8 : 1,
                    transition:"all 0.2s" }}>
                  {sent
                    ? <><FaCheck size={14}/> {isScheduleMode ? "Scheduled!" : "Sent!"}</>
                    : sending
                    ? <><span style={{ display:"inline-block", animation:"spin 1s linear infinite" }}>⟳</span> {isScheduleMode ? "Scheduling…" : "Sending…"}</>
                    : isScheduleMode
                    ? <>📅 Schedule for {new Date(`${schedDate}T${schedTime}`).toLocaleString("en-IN",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</>
                    : <><FaPaperPlane size={13}/> Send {files.length > 0 ? `+ ${files.length} file${files.length>1?"s":""}` : "Now"}</>
                  }
                </motion.button>

                {/* 📅 toggle button — only show when not sent/sending */}
                {!sent && !sending && (
                  <motion.button whileTap={{ scale:0.95 }}
                    onClick={() => { setShowSchedule(v => !v); if(showSchedule){ setSchedDate(""); setSchedTime(""); } }}
                    style={{ padding: isMobileView ? "14px 13px" : "13px 13px", borderRadius:14,
                      background: isScheduleMode ? "linear-gradient(135deg,rgba(124,58,237,0.25),rgba(168,85,247,0.15))"
                        : showSchedule ? "rgba(124,58,237,0.12)" : "rgba(124,58,237,0.07)",
                      border: isScheduleMode ? "2px solid rgba(124,58,237,0.5)"
                        : showSchedule ? "1.5px solid rgba(124,58,237,0.35)" : "1px solid rgba(124,58,237,0.2)",
                      cursor:"pointer", fontFamily:"inherit", fontSize:18,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      minWidth: isMobileView ? 50 : 48,
                      transition:"all 0.2s",
                      boxShadow: isScheduleMode ? "0 0 16px rgba(124,58,237,0.3)" : "none" }}
                    title="Schedule for later">
                    📅
                  </motion.button>
                )}
              </div>
            </div>
          );
        })()}

      </motion.div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// EMAIL TEMPLATE CARDS
// ═══════════════════════════════════════════════════════════════════
// eslint-disable-next-line no-unused-vars
function LetterTemplateCard({ template, index, onSelect, selected }) {
  const styles = [
    { color: "#f97316", bg: "#fff7ed", fold: "#fed7aa", label: "Professional", icon: "💼" },
    { color: "#ec4899", bg: "#fdf2f8", fold: "#fbcfe8", label: "Friendly",     icon: "😊" },
    { color: "#8b5cf6", bg: "#f5f3ff", fold: "#ddd6fe", label: "Brief",        icon: "⚡" },
  ];
  const s = styles[index] || styles[0];
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, type: "spring", stiffness: 200, damping: 20 }}
      onClick={() => onSelect(template)}
      className="cursor-pointer flex flex-col rounded-2xl overflow-hidden"
      style={{ background: "#fff", border: `2px solid ${selected ? s.color : "#ede9fe"}`,
        boxShadow: selected ? `0 4px 24px ${s.color}30` : "0 2px 8px rgba(0,0,0,0.06)",
        transform: selected ? "translateY(-3px)" : "none", transition: "all 0.2s" }}>
      <div className="h-1.5 w-full" style={{ background: `linear-gradient(90deg,${s.color},${styles[(index+1)%3].color})` }}/>
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <div className="flex items-center gap-2">
          <span>{s.icon}</span>
          <span className="text-xs font-bold" style={{ color: s.color }}>{s.label}</span>
        </div>
        {selected && <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: s.color }}><FaCheck size={8} color="#fff"/></div>}
      </div>
      <div className="mx-3 mb-3 rounded-xl p-3" style={{ background: s.bg, minHeight: "150px" }}>
        <div className="mb-2 pb-1.5" style={{ borderBottom: `1px solid ${s.fold}` }}>
          <p className="text-xs font-semibold truncate text-gray-800">{template.subject}</p>
        </div>
        <p className="text-xs text-gray-500" style={{ lineHeight: "1.6",
          display: "-webkit-box", WebkitLineClamp: 6, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {template.body}
        </p>
      </div>
      <div className="px-3 pb-3">
        <button onClick={e => { e.stopPropagation(); onSelect(template); }}
          className="w-full py-2 rounded-xl text-xs font-bold transition"
          style={{ background: selected ? s.color : s.bg, color: selected ? "#fff" : s.color, border: `1px solid ${s.color}40` }}>
          {selected ? "✓ Selected" : "Select & Edit"}
        </button>
      </div>
    </motion.div>
  );
}

function EmailTemplatesContainer({ templates, recipientEmail, onSendTemplate, userName, userEmail, accessToken, onSessionExpired, onScheduleEmail, onComposeOpen }) {
  const [localTemplates] = useState(() =>
    templates.map((t, i) => ({ ...t, id: i, to: recipientEmail || "", senderName: userName || "User", senderEmail: userEmail || "", accessToken: accessToken || "" }))
  );
  const [selectedId, setSelectedId] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeTemplate, setComposeTemplate] = useState(null);

  const handleSelect = t => { setSelectedId(t.id); setComposeTemplate(t); setComposeOpen(true); if(onComposeOpen) onComposeOpen(true); };

  const colors = [
    { bg: "rgba(59,130,246,0.18)", border: "rgba(59,130,246,0.5)", text: "#60a5fa", icon: "💼" },
    { bg: "rgba(34,197,94,0.18)",  border: "rgba(34,197,94,0.5)",  text: "#4ade80", icon: "😊" },
    { bg: "rgba(168,85,247,0.18)", border: "rgba(168,85,247,0.5)", text: "#c084fc", icon: "⚡" },
  ];

  return (
    <div className="w-full">
      {/* Mandatory notice */}
      <div className="flex items-center gap-2 mb-4 px-1 py-2 rounded-xl"
        style={{ background: "rgba(249,115,22,0.08)", border: "1px solid rgba(249,115,22,0.25)" }}>
        <span className="text-base">📋</span>
        <p className="text-sm font-semibold" style={{ color: "#fb923c" }}>
          You must choose one of these 3 templates before sending
        </p>
        <span className="ml-auto text-xs px-2 py-0.5 rounded-full font-bold"
          style={{ background: "rgba(249,115,22,0.2)", color: "#f97316" }}>REQUIRED</span>
      </div>

      {/* 3 template cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {localTemplates.map((t, i) => {
          const c = colors[i % 3];
          const isSelected = selectedId === t.id;
          return (
            <motion.div key={t.id} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
              onClick={() => handleSelect(t)} className="cursor-pointer rounded-2xl p-4 flex flex-col gap-2"
              style={{
                background: isSelected ? c.bg : "rgba(255,255,255,0.04)",
                border: `${isSelected ? "2px" : "1px"} solid ${isSelected ? c.border : "rgba(255,255,255,0.1)"}`,
                boxShadow: isSelected ? `0 0 18px ${c.border}` : "none",
                transition: "all 0.2s",
              }}>
              {/* Label */}
              <div className="flex items-center gap-2">
                <span className="text-xl">{c.icon}</span>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                  style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}` }}>
                  {t.label || ["Professional","Friendly","Brief"][i]}
                </span>
              </div>
              {/* Subject */}
              <p className="text-xs font-semibold text-white truncate">{t.subject}</p>
              {/* Body preview */}
              <p className="text-xs flex-1" style={{
                color: "#9ca3af", display: "-webkit-box",
                WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: "1.5"
              }}>{t.body}</p>
              {/* CTA */}
              <div className="flex items-center gap-1 mt-1 pt-2"
                style={{ borderTop: `1px solid ${c.border}`, color: c.text }}>
                <span className="text-xs font-bold">✏️ Click to edit &amp; send</span>
              </div>
            </motion.div>
          );
        })}
      </div>

      <p className="text-center text-xs mt-3" style={{ color: "#6b7280" }}>
        Click any template above → edit in compose window → send from your Gmail
      </p>

      <AnimatePresence>
        {composeOpen && composeTemplate && (
          <GmailComposeModal template={composeTemplate} senderName={userName || "User"} senderEmail={userEmail || ""}
            senderAccessToken={composeTemplate.accessToken || ""}
            onSend={onSendTemplate} onClose={() => { setComposeOpen(false); setSelectedId(null); if(onComposeOpen) onComposeOpen(false); }}
            onSessionExpired={onSessionExpired}
            onSchedule={onScheduleEmail}/>
        )}
      </AnimatePresence>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// IMAGE RESULT CARD — Canvas-based scene generator (zero external APIs, always works)
// ═══════════════════════════════════════════════════════════════════
function generateSceneOnCanvas(canvas, prompt) {
  const ctx = canvas.getContext('2d');
  const W = 768, H = 512;
  canvas.width = W; canvas.height = H;
  const p = (prompt || '').toLowerCase();
  const is = k => Array.isArray(k) ? k.some(w => p.includes(w)) : p.includes(k);

  // Detect if it's a PERSON/CHARACTER request (actor, person, portrait, famous person etc.)
  const isPerson   = is(['person','man','woman','actor','actress','singer','celebrity','portrait',
                         'face','character','hero','villain','warrior','queen','king','princess',
                         'shah rukh','hrithik','deepika','virat','modi','obama','elon','trump',
                         'robot','alien','wizard','ninja','samurai','superhero','anime']);
  const isSunset   = is(['sunset','golden hour','dusk','dawn','sunrise','twilight']);
  const isOcean    = is(['ocean','sea','beach','wave','shore','coastal','lake','river','waterfall']);
  const isForest   = is(['forest','tree','jungle','palm','tropical','nature','woods','rainforest']);
  const isCity     = is(['city','urban','building','skyline','skyscraper','town','street','downtown']);
  const isSpace    = is(['space','galaxy','star','cosmos','nebula','universe','planet','astronaut']);
  const isNight    = is(['night','moon','dark','midnight','evening','lunar','nocturnal']);
  const isSnow     = is(['snow','winter','ice','frost','blizzard','frozen','cold','arctic']);
  const isDesert   = is(['desert','sand','dune','sahara','arid','dry','cactus','oasis']);
  const isAbstract = is(['abstract','art','painting','pattern','colorful','geometric','neon','psychedelic','mandala']);
  const isFood     = is(['food','cake','pizza','coffee','fruit','restaurant','meal','dish']);

  // PERSON SCENE - stylized portrait with dramatic background
  if (isPerson) {
    // Dramatic gradient bg
    let bg = ctx.createLinearGradient(0, 0, W, H);
    bg.addColorStop(0, '#0a0a1a'); bg.addColorStop(0.5, '#1a0a2e'); bg.addColorStop(1, '#0a1a2e');
    ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

    // Bokeh background circles
    for (let i = 0; i < 40; i++) {
      const x = Math.random()*W, y = Math.random()*H, r = Math.random()*25+5;
      const hue = Math.random()*60+240; // blue-purple range
      ctx.fillStyle = `hsla(${hue},70%,60%,${Math.random()*0.15+0.03})`;
      ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI*2); ctx.fill();
    }

    // Spotlight / glow from top
    let spot = ctx.createRadialGradient(W/2, 0, 0, W/2, H*0.3, H*0.8);
    spot.addColorStop(0, 'rgba(200,150,255,0.25)');
    spot.addColorStop(0.5, 'rgba(100,100,255,0.08)');
    spot.addColorStop(1, 'transparent');
    ctx.fillStyle = spot; ctx.fillRect(0, 0, W, H);

    // Silhouette figure - stylized human shape
    const cx = W/2, cy = H*0.72;
    // Body shadow/glow
    let glow = ctx.createRadialGradient(cx, cy-60, 20, cx, cy-60, 180);
    glow.addColorStop(0, 'rgba(180,120,255,0.35)');
    glow.addColorStop(1, 'transparent');
    ctx.fillStyle = glow; ctx.fillRect(0, 0, W, H);

    // Head
    ctx.fillStyle = '#c8956c';
    ctx.beginPath(); ctx.arc(cx, cy-220, 52, 0, Math.PI*2); ctx.fill();
    // Hair
    ctx.fillStyle = '#2a1a0a';
    ctx.beginPath(); ctx.arc(cx, cy-240, 52, Math.PI, Math.PI*2); ctx.fill();
    ctx.fillRect(cx-52, cy-265, 104, 30);

    // Neck
    ctx.fillStyle = '#c8956c';
    ctx.fillRect(cx-15, cy-168, 30, 40);

    // Shoulders & torso (suit/clothing)
    ctx.fillStyle = '#1a1a2e';
    ctx.beginPath();
    ctx.moveTo(cx-90, cy); ctx.lineTo(cx-60, cy-130); ctx.lineTo(cx-20, cy-140);
    ctx.lineTo(cx+20, cy-140); ctx.lineTo(cx+60, cy-130); ctx.lineTo(cx+90, cy);
    ctx.closePath(); ctx.fill();

    // Suit lapels
    ctx.fillStyle = '#2a2a3e';
    ctx.beginPath(); ctx.moveTo(cx-20, cy-140); ctx.lineTo(cx-8, cy-80); ctx.lineTo(cx, cy-70); ctx.closePath(); ctx.fill();
    ctx.beginPath(); ctx.moveTo(cx+20, cy-140); ctx.lineTo(cx+8, cy-80); ctx.lineTo(cx, cy-70); ctx.closePath(); ctx.fill();

    // Shirt / tie
    ctx.fillStyle = '#ffffff'; ctx.fillRect(cx-8, cy-135, 16, 65);
    ctx.fillStyle = '#c0392b';
    ctx.beginPath(); ctx.moveTo(cx-5, cy-135); ctx.lineTo(cx+5, cy-135); ctx.lineTo(cx+4, cy-80); ctx.lineTo(cx, cy-70); ctx.lineTo(cx-4, cy-80); ctx.closePath(); ctx.fill();

    // Face features
    // Eyes
    ctx.fillStyle = '#1a0a00';
    ctx.beginPath(); ctx.ellipse(cx-18, cy-228, 8, 5, 0, 0, Math.PI*2); ctx.fill();
    ctx.beginPath(); ctx.ellipse(cx+18, cy-228, 8, 5, 0, 0, Math.PI*2); ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.beginPath(); ctx.arc(cx-16, cy-229, 2.5, 0, Math.PI*2); ctx.fill();
    ctx.beginPath(); ctx.arc(cx+20, cy-229, 2.5, 0, Math.PI*2); ctx.fill();
    // Smile
    ctx.strokeStyle = '#7a3a1a'; ctx.lineWidth = 2.5;
    ctx.beginPath(); ctx.arc(cx, cy-210, 18, 0.1, Math.PI-0.1); ctx.stroke();
    // Nose
    ctx.fillStyle = '#b07050';
    ctx.beginPath(); ctx.arc(cx, cy-218, 4, 0, Math.PI); ctx.fill();

    // Stage/floor
    let floor = ctx.createLinearGradient(0, cy, 0, H);
    floor.addColorStop(0, '#0f0f1f'); floor.addColorStop(1, '#050510');
    ctx.fillStyle = floor; ctx.fillRect(0, cy, W, H);

    // Floor reflection
    let refl = ctx.createLinearGradient(0, cy, 0, cy+80);
    refl.addColorStop(0, 'rgba(180,120,255,0.15)'); refl.addColorStop(1, 'transparent');
    ctx.fillStyle = refl; ctx.fillRect(0, cy, W, 80);

    // Stars/particles around
    for (let i = 0; i < 60; i++) {
      ctx.fillStyle = `rgba(255,255,255,${Math.random()*0.5+0.1})`;
      ctx.beginPath(); ctx.arc(Math.random()*W, Math.random()*H, Math.random()*1.5+0.3, 0, Math.PI*2); ctx.fill();
    }

    // Name label at bottom
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.fillRect(W*0.2, H-60, W*0.6, 42);
    ctx.fillStyle = 'rgba(200,150,255,0.9)';
    ctx.font = 'bold 16px sans-serif'; ctx.textAlign = 'center';
    ctx.fillText(prompt.substring(0, 50), W/2, H-34);

    // Vignette
    let vig = ctx.createRadialGradient(W/2, H/2, H*0.2, W/2, H/2, H*0.85);
    vig.addColorStop(0, 'transparent'); vig.addColorStop(1, 'rgba(0,0,0,0.6)');
    ctx.fillStyle = vig; ctx.fillRect(0, 0, W, H);
    return;
  }

  // ABSTRACT / ART
  if (isAbstract) {
    ctx.fillStyle = '#0a0a0a'; ctx.fillRect(0, 0, W, H);
    const hues = [0, 30, 60, 120, 180, 240, 300];
    for (let i = 0; i < 25; i++) {
      const hue = hues[Math.floor(Math.random()*hues.length)];
      const x = Math.random()*W, y = Math.random()*H, r = Math.random()*120+30;
      let g = ctx.createRadialGradient(x,y,0,x,y,r);
      g.addColorStop(0, `hsla(${hue},100%,65%,0.7)`);
      g.addColorStop(1, 'transparent');
      ctx.fillStyle = g; ctx.fillRect(0,0,W,H);
    }
    for (let i = 0; i < 8; i++) {
      ctx.strokeStyle = `hsla(${Math.random()*360},100%,70%,0.4)`;
      ctx.lineWidth = Math.random()*4+1;
      ctx.beginPath(); ctx.moveTo(Math.random()*W, Math.random()*H);
      for (let j=0;j<5;j++) ctx.quadraticCurveTo(Math.random()*W,Math.random()*H,Math.random()*W,Math.random()*H);
      ctx.stroke();
    }
    // Label
    ctx.fillStyle='rgba(255,255,255,0.3)'; ctx.font='italic 13px sans-serif'; ctx.textAlign='center';
    ctx.fillText(prompt.substring(0,60), W/2, H-16);
    return;
  }

  // FOOD
  if (isFood) {
    let bg = ctx.createLinearGradient(0,0,W,H);
    bg.addColorStop(0,'#1a0a00'); bg.addColorStop(1,'#2a1a0a');
    ctx.fillStyle=bg; ctx.fillRect(0,0,W,H);
    // Table
    ctx.fillStyle='#5c3a1a'; ctx.fillRect(0,H*0.6,W,H);
    ctx.fillStyle='#8b5e2a'; ctx.fillRect(0,H*0.6,W,8);
    // Plate
    let plate=ctx.createRadialGradient(W/2,H*0.5,0,W/2,H*0.5,130);
    plate.addColorStop(0,'#f0f0e8'); plate.addColorStop(1,'#d0d0c0');
    ctx.fillStyle=plate; ctx.beginPath(); ctx.ellipse(W/2,H*0.5,130,80,0,0,Math.PI*2); ctx.fill();
    ctx.strokeStyle='#c0c0b0'; ctx.lineWidth=3;
    ctx.beginPath(); ctx.ellipse(W/2,H*0.5,115,68,0,0,Math.PI*2); ctx.stroke();
    // Food items (colorful circles)
    [[0,'#e74c3c'],[60,'#f39c12'],[120,'#27ae60'],[180,'#8b4513'],[240,'#e67e22']].forEach(([a,col],i)=>{
      const angle=(a+i*15)*Math.PI/180, r=55;
      ctx.fillStyle=col; ctx.beginPath(); ctx.arc(W/2+r*Math.cos(angle),H*0.5+r*0.6*Math.sin(angle),22,0,Math.PI*2); ctx.fill();
    });
    // Steam
    for(let i=0;i<3;i++){
      ctx.strokeStyle=`rgba(255,255,255,${0.15+i*0.05})`; ctx.lineWidth=3;
      ctx.beginPath(); ctx.moveTo(W/2-30+i*30,H*0.35);
      ctx.quadraticCurveTo(W/2-40+i*30,H*0.25,W/2-25+i*30,H*0.15); ctx.stroke();
    }
    ctx.fillStyle='rgba(255,255,255,0.25)'; ctx.font='italic 13px sans-serif'; ctx.textAlign='center';
    ctx.fillText(prompt.substring(0,60), W/2, H-16);
    return;
  }

  // DESERT
  if (isDesert) {
    let sky=ctx.createLinearGradient(0,0,0,H);
    sky.addColorStop(0,'#1a4a8a'); sky.addColorStop(1,'#e8a040');
    ctx.fillStyle=sky; ctx.fillRect(0,0,W,H);
    // Sun
    ctx.fillStyle='#fff5aa'; ctx.beginPath(); ctx.arc(W*0.7,H*0.25,30,0,Math.PI*2); ctx.fill();
    // Sand dunes
    ['#c8a055','#d4a860','#b89045','#e0b870'].forEach((col,i)=>{
      ctx.fillStyle=col; ctx.beginPath(); ctx.moveTo(0,H);
      ctx.quadraticCurveTo(W*(0.2+i*0.15),H*(0.45+i*0.06),W*(0.5+i*0.1),H*(0.55+i*0.04));
      ctx.quadraticCurveTo(W*(0.75+i*0.05),H*(0.65+i*0.03),W,H*(0.58+i*0.05));
      ctx.lineTo(W,H); ctx.fill();
    });
    // Cactus
    ctx.fillStyle='#2d6620'; ctx.fillRect(W*0.15-12,H*0.35,24,H*0.35);
    ctx.fillRect(W*0.15-36,H*0.45,24,18); ctx.fillRect(W*0.15+12,H*0.5,24,18);
    ctx.fillStyle='rgba(255,255,255,0.2)'; ctx.font='italic 13px sans-serif'; ctx.textAlign='center';
    ctx.fillText(prompt.substring(0,60), W/2, H-16);
    return;
  }

  // ── Standard landscape scenes ───────────────────────────────────
  let skyColors;
  if (isSpace||isNight) skyColors=['#000008','#02021a','#040430'];
  else if (isSunset)    skyColors=['#0d0820','#6b1a3a','#c44b1a','#ff8c42','#ffb870'];
  else if (isSnow)      skyColors=['#b0c4d8','#ccdaec','#e4eef8'];
  else                  skyColors=['#0a2060','#1452a8','#3a7fd4','#7ab8e8'];

  let sg=ctx.createLinearGradient(0,0,0,H);
  skyColors.forEach((c,i)=>sg.addColorStop(i/(skyColors.length-1),c));
  ctx.fillStyle=sg; ctx.fillRect(0,0,W,H);

  if (isSpace||isNight) {
    for(let i=0;i<(isSpace?400:180);i++){
      ctx.fillStyle=`rgba(255,255,255,${Math.random()*0.85+0.15})`;
      ctx.beginPath(); ctx.arc(Math.random()*W,Math.random()*(isSpace?H:H*0.75),Math.random()*1.8+0.2,0,Math.PI*2); ctx.fill();
    }
    if(isSpace){
      ['rgba(120,0,180,0.13)','rgba(0,60,180,0.11)','rgba(180,0,60,0.1)'].forEach((c,i)=>{
        let ng=ctx.createRadialGradient(W*(0.25+i*0.25),H*(0.2+i*0.15),0,W*(0.25+i*0.25),H*(0.2+i*0.15),200);
        ng.addColorStop(0,c); ng.addColorStop(1,'transparent');
        ctx.fillStyle=ng; ctx.fillRect(0,0,W,H);
      });
    }
    ctx.fillStyle='#fffde0'; ctx.beginPath(); ctx.arc(W*0.78,H*0.16,44,0,Math.PI*2); ctx.fill();
    ctx.fillStyle='#01011a'; ctx.beginPath(); ctx.arc(W*0.82,H*0.14,38,0,Math.PI*2); ctx.fill();
  } else {
    const sx=W*(isSunset?0.5:0.74), sy=H*(isSunset?0.44:0.19);
    let sun=ctx.createRadialGradient(sx,sy,0,sx,sy,isSunset?170:110);
    sun.addColorStop(0,isSunset?'rgba(255,210,40,0.95)':'rgba(255,255,190,0.95)');
    sun.addColorStop(0.25,isSunset?'rgba(255,130,20,0.55)':'rgba(255,235,80,0.35)');
    sun.addColorStop(1,'transparent');
    ctx.fillStyle=sun; ctx.fillRect(0,0,W,H);
    ctx.fillStyle=isSunset?'#ffc800':'#fff5aa';
    ctx.beginPath(); ctx.arc(sx,sy,isSunset?34:24,0,Math.PI*2); ctx.fill();
  }

  if(!isNight&&!isSpace&&!isSnow){
    const dc=(x,y,s,op)=>{
      ctx.fillStyle=`rgba(255,255,255,${op})`;
      [[0,0,1],[0.65,0.12,0.78],[-0.55,0.12,0.72],[1.15,0.22,0.62],[-0.95,0.2,0.58]].forEach(([dx,dy,rs])=>{
        ctx.beginPath(); ctx.arc(x+dx*s*52,y+dy*s*42,s*rs*44,0,Math.PI*2); ctx.fill();
      });
    };
    if(!isSunset){dc(120,82,1,0.82); dc(400,58,0.9,0.76); dc(660,88,0.85,0.8);}
  }

  const gY=H*0.60;
  if(isOcean){
    let wg=ctx.createLinearGradient(0,gY,0,H);
    wg.addColorStop(0,isSunset?'#c04818':'#1558b0'); wg.addColorStop(1,isSunset?'#3a0c08':'#061c38');
    ctx.fillStyle=wg; ctx.fillRect(0,gY,W,H);
    for(let i=0;i<22;i++){
      ctx.fillStyle=`rgba(255,255,255,${Math.random()*0.15+0.04})`;
      ctx.beginPath(); ctx.ellipse(Math.random()*W,gY+Math.random()*(H-gY)*0.8,Math.random()*75+18,3,0,0,Math.PI*2); ctx.fill();
    }
    let sand=ctx.createLinearGradient(0,H*0.64,0,H); sand.addColorStop(0,'#d4a060'); sand.addColorStop(1,'#a07038');
    ctx.fillStyle=sand; ctx.beginPath(); ctx.moveTo(0,H*0.72);
    ctx.quadraticCurveTo(W*0.3,H*0.67,W*0.55,H*0.7); ctx.quadraticCurveTo(W*0.78,H*0.73,W,H*0.69);
    ctx.lineTo(W,H); ctx.lineTo(0,H); ctx.fill();
  } else if(isSnow){
    let sng=ctx.createLinearGradient(0,gY,0,H); sng.addColorStop(0,'#d5e5f2'); sng.addColorStop(1,'#b0c8de');
    ctx.fillStyle=sng; ctx.fillRect(0,gY,W,H);
    for(let i=0;i<90;i++){ctx.fillStyle='rgba(255,255,255,0.65)'; ctx.beginPath(); ctx.arc(Math.random()*W,gY+Math.random()*(H-gY),Math.random()*4.5+1,0,Math.PI*2); ctx.fill();}
  } else if(isCity){
    ctx.fillStyle='#08080f'; ctx.fillRect(0,gY,W,H);
    [[28,215,105],[155,265,88],[285,175,108],[428,242,92],[558,185,102],[678,228,86]].forEach(([x,h,w],i)=>{
      ctx.fillStyle=['#0c1c36','#16283e','#081428','#182c44'][i%4]; ctx.fillRect(x,H-h,w,h);
      ctx.fillStyle='rgba(255,255,140,0.7)';
      for(let wy=H-h+18;wy<H-18;wy+=22) for(let wx=x+10;wx<x+w-10;wx+=16)
        if(Math.random()>0.32) ctx.fillRect(wx,wy,9,13);
    });
  } else {
    let gg=ctx.createLinearGradient(0,gY,0,H); gg.addColorStop(0,'#2a6410'); gg.addColorStop(1,'#163808');
    ctx.fillStyle=gg; ctx.fillRect(0,gY,W,H);
    ctx.fillStyle='#336014'; ctx.beginPath(); ctx.moveTo(0,H);
    ctx.quadraticCurveTo(W*0.3,gY-15,W*0.55,gY+12); ctx.quadraticCurveTo(W*0.78,gY+28,W,gY+5);
    ctx.lineTo(W,H); ctx.fill();
  }

  if(isForest||isOcean){
    const palm=(x,bY,h,lean)=>{
      ctx.strokeStyle='#5c3818'; ctx.lineWidth=11; ctx.lineCap='round';
      const tx=x+lean*h*0.14, ty=bY-h;
      ctx.beginPath(); ctx.moveTo(x,bY); ctx.quadraticCurveTo(x+lean*18,bY-h*0.5,tx,ty); ctx.stroke();
      ctx.fillStyle='#246020';
      for(let a=-55;a<=55;a+=22){
        ctx.save(); ctx.translate(tx,ty); ctx.rotate((a+lean*8)*Math.PI/180);
        ctx.beginPath(); ctx.ellipse(44,0,58,11,0,0,Math.PI*2); ctx.fill(); ctx.restore();
      }
    };
    palm(W*0.1,H*0.96,205,-1); palm(W*0.89,H*0.96,192,1);
    if(isForest){palm(W*0.32,H*0.98,162,0); palm(W*0.68,H*0.97,178,0.4);}
  }

  let vig=ctx.createRadialGradient(W/2,H/2,H*0.26,W/2,H/2,H*0.92);
  vig.addColorStop(0,'transparent'); vig.addColorStop(1,'rgba(0,0,0,0.52)');
  ctx.fillStyle=vig; ctx.fillRect(0,0,W,H);

  ctx.fillStyle='rgba(255,255,255,0.22)'; ctx.font='italic 12px sans-serif'; ctx.textAlign='center';
  ctx.fillText(prompt.length>65?prompt.substring(0,65)+'…':prompt, W/2, H-14);
}


function ImageResultCard({ prompt }) {
  const canvasRef = useRef(null);
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    if (!canvasRef.current || !prompt) return;
    try {
      generateSceneOnCanvas(canvasRef.current, prompt);
      setRendered(true);
    } catch(e) {
      console.error('Canvas render error:', e);
    }
  }, [prompt]);

  const download = () => {
    if (!canvasRef.current) return;
    const a = document.createElement('a');
    a.href = canvasRef.current.toDataURL('image/png');
    a.download = 'santharion-ai-' + Date.now() + '.png';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <div className="w-full flex flex-col gap-3">
      <div className="rounded-2xl overflow-hidden relative"
        style={{ border: "1px solid rgba(249,115,22,0.3)", background: "#050510" }}>
        {!rendered && (
          <div className="absolute inset-0 flex items-center justify-center gap-3 z-10">
            <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
              className="w-8 h-8 rounded-full border-2 border-transparent"
              style={{ borderTopColor: "#f97316", borderRightColor: "#ec4899" }}/>
            <span className="text-sm" style={{ color: "#9ca3af" }}>Drawing scene…</span>
          </div>
        )}
        <canvas ref={canvasRef} className="w-full h-auto rounded-2xl block" style={{ display: rendered ? 'block' : 'none' }}/>
      </div>
      {rendered && (
        <div className="flex gap-2">
          <button onClick={download}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold flex-1 justify-center text-white"
            style={{ background: "linear-gradient(135deg,#f97316,#ec4899)" }}>
            <FaDownload size={11}/> Download PNG
          </button>
        </div>
      )}
      <p className="text-xs text-center" style={{ color: "#6b7280" }}>
        🎨 AI scene · {(prompt||'').substring(0,70)}{(prompt||'').length>70?'…':''}
      </p>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// MAIN — SANTHARION AI
// ═══════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════
// INBOX PANEL
// ═══════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════
// INBOX ITEM — shows sent or received message with reply button
// ═══════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════
// SENT EMAIL ITEM — shows a sent email in the Sent panel
// ═══════════════════════════════════════════════════════════════════
function SentEmailItem({ email }) {
  const [expanded, setExpanded] = useState(false);
  const timeAgo = (ts) => {
    if (!ts) return "";
    const diff = Date.now() - new Date(ts).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1)  return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  return (
    <div onClick={() => setExpanded(e => !e)}
      style={{ padding:"12px 14px", borderRadius:12, marginBottom:6, cursor:"pointer",
        background:"rgba(34,197,94,0.05)", border:"1px solid rgba(34,197,94,0.15)" }}>
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <div style={{ width:34, height:34, borderRadius:10, flexShrink:0,
          background:"linear-gradient(135deg,#22c55e,#16a34a)",
          display:"flex", alignItems:"center", justifyContent:"center", fontSize:15 }}>📤</div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:12, fontWeight:700, color:"#e2e8f0",
            overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
            To: {email.to}
          </div>
          <div style={{ fontSize:11, color:"#6b7280", marginTop:1,
            overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
            {email.subject}
          </div>
        </div>
        <span style={{ fontSize:10, color:"#374151", flexShrink:0 }}>{timeAgo(email.sentAt)}</span>
      </div>
      {expanded && (
        <div style={{ marginTop:10, paddingTop:10, borderTop:"1px solid rgba(255,255,255,0.06)",
          fontSize:12, color:"#9ca3af", whiteSpace:"pre-wrap", lineHeight:1.6 }}>
          {email.body?.slice(0, 400)}{email.body?.length > 400 ? "…" : ""}
        </div>
      )}
    </div>
  );
}

export default function AiAssistant() {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("learnhubai_user") || "null"); }
    catch { return null; }
  });

  const defaultMsg = [{
    role: "assistant", type: "text", animate: false,
    content:
      "👋 **Hey! I'm Santharion AI**\n\n" +
      "I help you write and send professional emails in seconds — straight from your Gmail.\n\n" +
      "Just tell me what you want to say. I'll generate **3 perfectly toned templates** for you to choose from.\n\n" +
      "📬 **Sign in with Google** to get started — free, instant, no password stored.",
  }];

  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem("santharion_chat");
      if (saved) {
        const parsed = JSON.parse(saved);
        // strip animate flags so nothing re-animates on restore
        return parsed.map(m => ({ ...m, animate: false }));
      }
    } catch(e) {}
    return defaultMsg;
  });

  const [input,   setInput]   = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef  = useRef(null);
  const chatAreaRef = useRef(null);
  const userScrolledUp = useRef(false); // true when user manually scrolled up

  // Chat history — list of past saved chats shown in sidebar
  const [chatSessions, setChatSessions] = useState(() => {
    try { return JSON.parse(localStorage.getItem("santharion_sessions") || "[]"); } catch(e) { return []; }
  });

  // Feature 3: AI style learning
  const [styleProfile,     setStyleProfile]     = useState(null);
  // Feature 4: Follow-up reminders
  const [followUps,        setFollowUps]        = useState([]);
  // Loop: tracked email threads
  const [trackedThreads,   setTrackedThreads]   = useState(() => {
    try { return JSON.parse(localStorage.getItem("santharion_threads") || "[]"); } catch(e) { return []; }
  });
  // Feature panel visibility
  const [showFeaturePanel, setShowFeaturePanel] = useState(false); // eslint-disable-line no-unused-vars
  // Inbox — stores all received replies

  const [showSent,   setShowSent]   = useState(false);
  const [sentEmails, setSentEmails] = useState(() => {
    try { return JSON.parse(localStorage.getItem("santharion_sent") || "[]"); } catch(e) { return []; }
  });

  // Mobile sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [composeModalOpen, setComposeModalOpen] = useState(false); // hides input bar when compose is open
  // Reply alert toast
  const [replyAlert, setReplyAlert] = useState(null); // { from, subject, threadId }
  const [isMobile,    setIsMobile]    = useState(() => typeof window !== "undefined" && window.innerWidth < 769);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 769);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ── NEW FEATURES STATE ──
  const [activeTone,    setActiveTone]    = useState("Professional"); // Tone Switcher
  const [emailScore,    setEmailScore]    = useState(null);           // Email Quality Score
  const [emailMemory,   setEmailMemory]   = useState(() => {          // Email Memory
    try { return JSON.parse(localStorage.getItem("santharion_memory") || "{}"); } catch(e) { return {}; }
  });
  const [showInsights,  setShowInsights]  = useState(true);           // Right panel toggle

  const [scheduleHint,  setScheduleHint]  = useState(null);          // Smart Scheduler hint
  const [scheduledEmails, setScheduledEmails] = useState(() => {
    try { return JSON.parse(localStorage.getItem("santharion_scheduled") || "[]"); } catch(e) { return []; }
  });
  const [showScheduled, setShowScheduled] = useState(false);

  const [showOnboarding,setShowOnboarding]= useState(() => {
    try { return !localStorage.getItem("santharion_onboarded"); } catch(e) { return true; }
  });
  const [onboardingStep, setOnboardingStep] = useState(0); // 0=welcome, 1=howto, 2=features, 3=ready

  // ── Persist state to localStorage on every change ──
  useEffect(() => {
    try {
      // Only save last 30 messages, strip large binary content
      const toSave = messages.slice(-30).map(m => ({
        ...m, animate: false,
        // Don't persist image blobs or large file data
        ...(m.type === "image" ? { content: null } : {}),
      }));
      localStorage.setItem("santharion_chat", JSON.stringify(toSave));
    } catch(e) {}
  }, [messages]);

  useEffect(() => {
    try { localStorage.setItem("santharion_threads", JSON.stringify(trackedThreads.slice(-50))); } catch(e) {}
  }, [trackedThreads]);

  useEffect(() => {
    try { localStorage.setItem("santharion_sent", JSON.stringify(sentEmails.slice(-100))); } catch(e) {}
  }, [sentEmails]);



  useEffect(() => {
    try { localStorage.setItem("santharion_scheduled", JSON.stringify(scheduledEmails)); } catch(e) {}
  }, [scheduledEmails]);

  // Persist scheduled emails
  useEffect(() => {
    try { localStorage.setItem("santharion_scheduled", JSON.stringify(scheduledEmails)); } catch(e) {}
  }, [scheduledEmails]);

  // ── Scheduled email processor — checks every 60s, marks sent ONLY after API succeeds ──
  useEffect(() => {
    const fireScheduled = async () => {
      const now = Date.now();
      // Read current scheduled list without using setState callback (avoids stale closure)
      let stored = [];
      try { stored = JSON.parse(localStorage.getItem("santharion_scheduled") || "[]"); } catch(e) { return; }

      const due = stored.filter(e => !e.sent && e.sendAt <= now);
      if (!due.length) return;

      for (const email of due) {
        if (!email.accessToken) {
          // No token — mark failed so it doesn't retry forever
          setScheduledEmails(prev => prev.map(e => e.id === email.id ? {...e, sent:true, failed:true} : e));
          continue;
        }
        try {
          const res = await fetch("/api/email/send-tracked", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              to: email.to, subject: email.subject, body: email.body,
              accessToken: email.accessToken, fromEmail: email.fromEmail, fromName: email.fromName,
            }),
          });
          const data = await res.json();
          if (data.success || res.ok) {
            // ✅ API succeeded — NOW mark as sent
            const threadId  = data.data?.threadId  || null;
            const messageId = data.data?.messageId || null;
            setScheduledEmails(prev => prev.map(e => e.id === email.id ? {...e, sent:true, failed:false, threadId} : e));
            setSentEmails(prev => [{ to:email.to, subject:email.subject, sentAt:new Date().toISOString(), scheduled:true, threadId }, ...prev]);
            // ✅ Register threadId so inbox reply detection picks it up
            if (threadId) {
              setTrackedThreads(prev => {
                if (prev.find(t => t.threadId === threadId)) return prev;
                return [...prev, { threadId, messageId, to: email.to, subject: email.subject, sentAt: new Date().toISOString() }];
              });
            }
            setReplyAlert({ from:"📅 Scheduled email sent!", subject:`"${email.subject}" delivered to ${email.to}`, threadId:null });
            setTimeout(() => setReplyAlert(null), 6000);
          } else {
            // API returned error — keep as pending so user knows it failed
            console.error("Scheduled send API error:", data);
            setScheduledEmails(prev => prev.map(e => e.id === email.id ? {...e, failed:true, failReason: data.message || "Send failed"} : e));
          }
        } catch(err) {
          console.error("Scheduled send network error:", err);
          setScheduledEmails(prev => prev.map(e => e.id === email.id ? {...e, failed:true, failReason:"Network error"} : e));
        }
      }
    };

    fireScheduled(); // check immediately on load
    const interval = setInterval(fireScheduled, 60000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Voice input
  const voice = useVoiceInput((transcript) => {
    setInput(prev => prev ? prev + " " + transcript : transcript);
  });

  // Helper — are we near the bottom of chat?
  const isNearBottom = () => {
    const el = chatAreaRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  // Detect when user manually scrolls up
  useEffect(() => {
    const el = chatAreaRef.current;
    if (!el) return;
    const onScroll = () => {
      userScrolledUp.current = !isNearBottom();
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  // Smart scroll — only scroll down if user is already near bottom OR new message just sent
  const scrollToBottom = (force = false) => {
    if (force || !userScrolledUp.current) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      userScrolledUp.current = false;
    }
  };

  // Auto-scroll on new messages — respect user scroll position
  useEffect(() => {
    setTimeout(() => scrollToBottom(), 50);
  }, [messages, loading]);

  // Scroll during typewriter — only if not scrolled up
  useEffect(() => {
    const interval = setInterval(() => {
      const hasAnimating = messages.some(m => m.animate);
      if (hasAnimating && !userScrolledUp.current) {
        bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      }
    }, 300);
    return () => clearInterval(interval);
  }, [messages]);

  // ── Feature 3: Load style profile when user logs in ─────────────
  useEffect(() => {
    if (user?.email) {
      fetch(`/api/email/style/${encodeURIComponent(user.email)}`)
        .then(r => r.json())
        .then(d => { if (d.success && d.data?.emailsSent > 0) setStyleProfile(d.data); })
        .catch(() => {});
    }
  }, [user]);

  // Keep a ref so the interval always reads latest trackedThreads & user
  const trackedThreadsRef = useRef([]);
  const userRef           = useRef(null);

  useEffect(() => { trackedThreadsRef.current = trackedThreads; }, [trackedThreads]);
  useEffect(() => { userRef.current = user; }, [user]);

  // ── Inbox: Poll every 10 seconds — uses refs so interval never goes stale ──
  const checkRepliesRef = useRef(null);

  useEffect(() => {
    const checkReplies = async () => {
      const currentUser = userRef.current;
      if (!currentUser?.accessToken || !currentUser?.email) return;
      try {
        const res = await fetch("/api/email/check-all-replies", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ accessToken: currentUser.accessToken, userEmail: currentUser.email })
        });
        const data = await res.json();

        if (!data.success) {
          if (data.message?.toLowerCase().includes("session expired")) {
            const newToken = await refreshAccessToken();
            if (newToken) setTimeout(() => { if (checkRepliesRef.current) checkRepliesRef.current(); }, 1000);
          }
        } else if (data.data?.length > 0) {
          data.data.forEach(reply => {
            const from    = reply.replyFrom || "Recipient";
            const subject = reply.subject   || "your email";
            const snippet = reply.snippet   || "";
            setTrackedThreads(prev => prev.filter(t => t.threadId !== reply.threadId));
            // Inject a chat message notifying the user of the reply
            setMessages(prev => {
              const alreadyNotified = prev.some(m => m.threadId === reply.threadId && m.type === "reply_notification");
              if (alreadyNotified) return prev;
              return [...prev, {
                role: "assistant",
                type: "reply_notification",
                threadId: reply.threadId,
                animate: true,
                content: "📬 **You got a reply from " + from + "!**\n\n📌 **Subject:** Re: " + subject + (snippet ? "\n\n💬 *\"" + snippet.slice(0,200) + (snippet.length>200?"…":"") + "\"*" : "") + "\n\nNo action needed — your email conversation is happening in your Gmail inbox.",
              }];
            });
          });
        }
      } catch(e) { console.warn("[Reply check] Error:", e.message); }
    };

    checkRepliesRef.current = checkReplies;

    // Check immediately then every 10 seconds
    // If user is already logged in (localStorage), load their threads from server immediately
    const currentUser = userRef.current;
    if (currentUser?.email) {
      const loadThreads = (retryCount = 0) => {
        fetch("/api/email/my-threads", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userEmail: currentUser.email })
        }).then(r => r.json()).then(d => {
          if (d.success && d.data?.length > 0) {
            setTrackedThreads(d.data.map(t => ({ threadId: t.threadId, to: t.to, subject: t.subject })));
            console.log("[Threads] Loaded", d.data.length, "tracked threads from server");
          }
        }).catch(() => {
          // Server still waking up — retry after 15 seconds (max 3 retries)
          if (retryCount < 3) {
            console.log("[Threads] Server waking up, retrying in 15s...");
            setTimeout(() => loadThreads(retryCount + 1), 15000);
          }
        });
      };
      loadThreads();

      // Also load scheduled emails
      fetch(`/api/email/scheduled/${encodeURIComponent(currentUser.email)}`)
        .then(r => r.json()).then(d => {
          if (d.success && d.data?.length > 0) {
            setScheduledEmails(d.data.map(e => ({
              id: e.id, to: e.to, subject: e.subject, body: e.body,
              sendAt: new Date(e.sendAt).getTime(),
              fromEmail: e.fromEmail, fromName: e.fromName,
              sent: e.status === "SENT", failed: e.status === "FAILED",
              failReason: e.failReason || "", serverManaged: true,
            })));
          }
        }).catch(() => {});
    }

    checkReplies();
    const iv = setInterval(checkReplies, 10 * 1000);

    // ── Keep Render backend awake — ping every 14 minutes ──────────
    // Render free tier sleeps after 15 min inactivity → causes "Failed to fetch"
    const pingInterval = setInterval(() => {
      fetch("/api/visitors/track", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ newSession: false })
      }).catch(() => {}); // silent — just keeping server awake
    }, 14 * 60 * 1000); // every 14 minutes
    return () => { clearInterval(iv); clearInterval(pingInterval); };
  }, []); // ← empty deps — runs once, uses refs for fresh data

  // emailSent event removed — sendEmailTemplate handles success message directly

  // ── Feature 4: Check follow-up reminders every minute ───────────
  useEffect(() => {
    const iv = setInterval(() => {
      const now = Date.now();
      setFollowUps(prev => {
        const due = prev.filter(f => f.remindAt <= now);
        due.forEach(f => {
          setMessages(msgs => [...msgs, {
            role: "assistant", type: "followup_reminder", animate: true,
            content: `⏰ **Follow-up reminder!**

You sent an email to **${f.to}** about **"${f.subject}"** ${f.label} and haven't heard back yet.

Would you like me to write a follow-up?`,
            followUpData: f,
          }]);
        });
        return prev.filter(f => f.remindAt > now);
      });
    }, 60000);
    return () => clearInterval(iv);
  }, []);

  // OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("loginSuccess") === "true") {
      const u = {
        email:       params.get("email")       || "",
        name:        params.get("name")        || "User",
        picture:     params.get("picture")     || "",
        accessToken: params.get("accessToken") || "",
      };
      setUser(u);
      localStorage.setItem("learnhubai_user", JSON.stringify(u));
      window.history.replaceState({}, "", "/ai-assistant");

      // Check if there was a pending email request before login
      const pendingEmail = sessionStorage.getItem("santharion_pending_email");
      sessionStorage.removeItem("santharion_pending_email");
      // Load pending threads from MongoDB + immediately check for replies
      fetch("/api/email/my-threads", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userEmail: u.email })
      }).then(r => r.json()).then(d => {
        if (d.success && d.data?.length > 0) {
          setTrackedThreads(d.data.map(t => ({ threadId: t.threadId, to: t.to, subject: t.subject })));
        }
        setTimeout(() => { if (checkRepliesRef.current) checkRepliesRef.current(); }, 1000);
      }).catch(() => {
        setTimeout(() => { if (checkRepliesRef.current) checkRepliesRef.current(); }, 1500);
      });

      // Load scheduled emails from server so they show even after page reload
      fetch(`/api/email/scheduled/${encodeURIComponent(u.email)}`)
        .then(r => r.json()).then(d => {
          if (d.success && d.data?.length > 0) {
            setScheduledEmails(d.data.map(e => ({
              id:        e.id,
              to:        e.to,
              subject:   e.subject,
              body:      e.body,
              sendAt:    new Date(e.sendAt).getTime(),
              fromEmail: e.fromEmail,
              fromName:  e.fromName,
              sent:      e.status === "SENT",
              failed:    e.status === "FAILED",
              failReason: e.failReason || "",
              serverManaged: true, // flag — server will send, no frontend timer needed
            })));
          }
        }).catch(() => {});

      if (pendingEmail) {
        const pending = JSON.parse(pendingEmail);
        setMessages(prev => [...prev, {
          role: "assistant", type: "success", animate: true,
          content: "✅ **Signed in as " + u.name + "!** Gmail connected 🎉\n\n" +
            "I remembered your email request! Resuming now...",
        }]);
        // Restore the pending message as if user just sent it
        setTimeout(() => {
          setMessages(prev => [...prev, { role: "user", type: "text", content: pending.originalMessage, animate: false }]);
          // Trigger processing with the saved context
          fetch("/api/ai/chat", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              messages: [...(pending.history || []), { role: "user", content: pending.originalMessage }],
              userEmail: u.email, userName: u.name, accessToken: u.accessToken,
            })
          }).then(r => r.json()).then(data => {
            if (data.success && data.data) {
              const r = data.data;
              if (r.type === "email_templates") {
                let parsedTemplates = [];
                try {
                  let raw = r.templatesJson || "";
                  let parsed = null;
                  try { parsed = JSON.parse(raw); } catch { parsed = null; }
                  if (typeof parsed === "string") { try { parsed = JSON.parse(parsed); } catch { parsed = null; } }
                  if (parsed) {
                    parsedTemplates = Array.isArray(parsed) ? parsed : (parsed.templates || []);
                  }
                  parsedTemplates = parsedTemplates.map((t,i) => ({
                    label: t.label || ["Professional","Friendly","Brief"][i],
                    subject: t.subject || "Email",
                    body: t.body || "",
                  })).filter(t => t.body && !t.body.startsWith("{"));
                } catch(e) { console.error(e); }
                setMessages(prev => [...prev, { role: "assistant", type: "email_templates", animate: false,
                  content: r.message, templates: parsedTemplates, recipientEmail: r.recipientEmail || "" }]);
              } else {
                setMessages(prev => [...prev, { role: "assistant", type: r.type || "text",
                  content: r.message || "Done!", animate: true }]);
              }
            }
          }).catch(() => {});
        }, 800);
      } else {
        setMessages(prev => [...prev, {
          role: "assistant", type: "success", animate: true,
          content: "✅ **Signed in as " + u.name + "!**\n\n" +
            "Gmail **(" + u.email + ")** connected 🎉\n\n" +
            "I can now send real emails and fill forms for you. What would you like to do?",
        }]);
      }
    }
    if (params.get("error")) {
      window.history.replaceState({}, "", "/ai-assistant");
      setMessages(prev => [...prev, {
        role: "assistant", type: "error", animate: true,
        content: "⚠️ Login failed: " + decodeURIComponent(params.get("error")),
      }]);
    }
  }, []);

  const handleSignIn = async () => {
    try {
      const res  = await fetch("/api/auth/google/url");
      const data = await res.json();
      // AuthController returns { url: "..." } directly
      const redirectUrl = data.url || (data.success && data.data);
      if (redirectUrl) window.location.href = redirectUrl;
      else alert("Could not get Google sign-in URL. Please try again.");
    } catch (err) { alert("Login error: " + err.message); }
  };

  // ── Auto-refresh token on app load if user is logged in ──────────
  useEffect(() => {
    const saved = localStorage.getItem("learnhubai_user");
    if (!saved) return;
    // Silently refresh on page load — token may have expired while tab was closed
    setTimeout(() => {
      if (userRef.current?.email) {
        fetch("/api/auth/refresh", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: userRef.current.email })
        }).then(r => r.json()).then(data => {
          if (data.success && data.data?.accessToken) {
            const refreshed = { ...userRef.current, accessToken: data.data.accessToken };
            setUser(refreshed);
            localStorage.setItem("learnhubai_user", JSON.stringify(refreshed));
            userRef.current = refreshed;
            console.log("[Auth] Token auto-refreshed on page load");
          }
        }).catch(() => {}); // silent — don't log out
      }
    }, 2000); // wait 2s for server to wake up first
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Silently refresh Google access token ─────────────────────────
  const refreshAccessToken = useCallback(async () => {
    const currentUser = userRef.current;
    if (!currentUser?.email) return null;
    try {
      const res = await fetch("/api/auth/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: currentUser.email })
      });
      const data = await res.json();
      if (data.success && data.data?.accessToken) {
        const refreshed = { ...currentUser, accessToken: data.data.accessToken };
        setUser(refreshed);
        localStorage.setItem("learnhubai_user", JSON.stringify(refreshed));
        userRef.current = refreshed;
        console.log("[Auth] Access token refreshed silently");
        return data.data.accessToken;
      } else {
        // Don't log out — server may just be waking up, user stays logged in
        console.warn("[Auth] Refresh failed (server may be waking up):", data.message);
        return null;
      }
    } catch(e) {
      // Network error — server sleeping, don't log out
      console.warn("[Auth] Refresh network error (server may be sleeping):", e.message);
      return null;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSignOut = () => {
    localStorage.removeItem("learnhubai_user");
    setUser(null);
    setMessages(prev => [...prev, {
      role: "assistant", type: "text", animate: true,
      content: "👋 Signed out! Sign in again anytime to send emails or fill forms.",
    }]);
  };

  // Called whenever backend returns "Session expired"
  const handleSessionExpired = () => {
    localStorage.removeItem("learnhubai_user");
    setUser(null);
    // FIX: save current chat to history, then start a new chat
    setMessages(prev => {
      saveCurrentChatToHistory(prev);
      return [{
        role: "assistant", type: "text", animate: true,
        content: `🔒 **Your session expired** — your chat has been saved to history.\n\nPlease **Sign in with Google** again to continue sending emails.\n\n💾 You can find your previous conversation in the sidebar.`,
      }];
    });
  };

  // Send email with thread tracking + follow-up prompt
  const sendEmailTemplate = async (to, subject, body, followUpDays = null, threadId = null, attachmentThreadId = null) => {
    // If replying in thread
    if (threadId) {
      const res = await fetch("/api/email/reply-thread", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to, subject, body, threadId,
          fromEmail: user?.email || "", fromName: user?.name || "",
          accessToken: user?.accessToken || "" }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Reply failed");
      setMessages(prev => [...prev, {
        role: "assistant", type: "success", animate: true, content: data.data.message,
      }]);
      return;
    }

    // New email — use tracked endpoint
    console.log("[sendEmailTemplate] Calling send-tracked. accessToken empty:", !user?.accessToken, "to:", to);
    const res = await fetch("/api/email/send-tracked", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ to, subject, body,
        fromEmail: user?.email || "", fromName: user?.name || "",
        accessToken: user?.accessToken || "" }),
    });
    console.log("[send-tracked] HTTP status:", res.status);
    const data = await res.json();
    console.log("[send-tracked] response:", JSON.stringify(data));
    if (!data.success) {
      if (data.message && data.message.toLowerCase().includes("session expired")) {
        handleSessionExpired();
        return;
      }
      throw new Error(data.message || "Send failed");
    }

    const { threadId: newThreadId, message } = data.data;

    // Use threadId from attachment if normal send didn't return one
    const finalThreadId = newThreadId || attachmentThreadId;

    // Add to tracked threads for reply detection
    if (finalThreadId) {
      setTrackedThreads(prev => [...prev, {
        threadId: finalThreadId, to, subject, sentAt: Date.now()
      }]);
    }

    // Add to sent emails sidebar
    const sentItem = {
      id: `sent_${Date.now()}`,
      type: "sent",
      from: user?.email || "You",
      to,
      subject,
      snippet: body.slice(0, 120),
      body,
      threadId: finalThreadId,
      sentAt: Date.now(),
    };
    setSentEmails(prev => [sentItem, ...prev]);

    // Calculate quality score for sent email
    const score = calcEmailScore(body);
    setEmailScore(score);

    // Update email memory
    updateEmailMemory(to, subject);

    // Smart scheduler hint
    setScheduleHint(getScheduleHint(to));

    setMessages(prev => [...prev, {
      role: "assistant", type: "success_with_options", animate: true,
      content: message || "✅ Email sent successfully!",
      to, subject, threadId: finalThreadId, score,
    }]);

    // Schedule follow-up if requested
    if (followUpDays && followUpDays > 0) {
      const remindAt = Date.now() + followUpDays * 24 * 60 * 60 * 1000;
      const labels = { 1:"yesterday", 2:"2 days ago", 3:"3 days ago", 7:"a week ago" };
      setFollowUps(prev => [...prev, {
        to, subject, threadId: newThreadId,
        remindAt, label: labels[followUpDays] || `${followUpDays} days ago`
      }]);
      setMessages(prev => [...prev, {
        role: "assistant", type: "text", animate: true,
        content: `⏰ Got it! I'll remind you to follow up with **${to}** in **${followUpDays} day${followUpDays>1?"s":""}** if no reply.`,
      }]);
    }
  };

  // ── Email Quality Score calculator ──
  const calcEmailScore = (body) => {
    if (!body) return null;
    const words = body.split(/\s+/).length;
    const hasGreeting = /dear|hi |hello|hey/i.test(body);
    const hasClosing  = /regards|sincerely|thanks|best|cheers/i.test(body);
    const hasPunctuation = (body.match(/[.!?]/g)||[]).length > 2;
    const notTooShort = words > 20;
    const notTooLong  = words < 300;
    const clarity  = Math.min(100, Math.round(60 + (hasGreeting?10:0) + (hasClosing?10:0) + (hasPunctuation?10:0) + (notTooShort?10:0)));
    const tone     = Math.min(100, Math.round(55 + (hasGreeting?15:0) + (hasClosing?15:0) + (notTooLong?15:0)));
    const urgency  = Math.min(100, Math.round(50 + (/urgent|asap|immediately|deadline|today/i.test(body)?30:0) + (words>40?20:0)));
    const overall  = Math.round((clarity + tone + urgency) / 3);
    return { overall, clarity, tone, urgency };
  };

  // ── Reply Mood Detector ──
;
  const moodEmoji = (mood) => ({ positive:"😊 Positive", negative:"😠 Negative", urgent:"🚨 Urgent", neutral:"💬 Neutral" }[mood] || "💬");
  const moodColor = (mood) => ({ positive:"#4ade80", negative:"#f87171", urgent:"#fbbf24", neutral:"#9ca3af" }[mood] || "#9ca3af");

  // ── Email Memory — update when sending ──
  const updateEmailMemory = (to, subject) => {
    if (!to) return;
    setEmailMemory(prev => {
      const key = to.toLowerCase();
      const entry = prev[key] || { to, count:0, subjects:[], lastAt:0 };
      const updated = { ...prev, [key]: {
        to, count: entry.count + 1,
        subjects: [subject, ...(entry.subjects||[])].slice(0,5),
        lastAt: Date.now()
      }};
      try { localStorage.setItem("santharion_memory", JSON.stringify(updated)); } catch(e) {}
      return updated;
    });
  };

  // ── Smart Send Scheduler — real time-based suggestion ──
  const getScheduleHint = (toEmail) => {
    if (!toEmail) return null;
    const now = new Date();
    const hour = now.getHours();
    const day  = now.getDay(); // 0=Sun 6=Sat

    // Known international domain → timezone hint
    const domain = (toEmail.split("@")[1] || "").toLowerCase();
    const intlDomains = {
      "gmail.com":     null,   // could be anywhere, skip
      "yahoo.com":     "US",
      "outlook.com":   null,
      "hotmail.com":   null,
      "aol.com":       "US",
      "icloud.com":    "US",
    };
    const countryHints = {
      ".uk":  "UK — Best: 9–11 AM BST",
      ".au":  "Australia — Best: 9–11 AM AEST",
      ".de":  "Germany — Best: 9–11 AM CET",
      ".ca":  "Canada — Best: 9–11 AM EST",
      ".sg":  "Singapore — Best: 9–11 AM SGT",
    };
    for (const [tld, hint] of Object.entries(countryHints)) {
      if (domain.endsWith(tld)) return `📅 ${hint}`;
    }

    // Local time-based advice
    if (day === 0 || day === 6) return "📅 It's the weekend — best to send Monday 9 AM";
    if (hour < 7)  return "📅 Too early — best time: 9–11 AM today";
    if (hour >= 7 && hour < 11)  return "📅 Perfect time to send! Open rates are highest now";
    if (hour >= 11 && hour < 14) return "📅 Good time — before lunch break";
    if (hour >= 14 && hour < 17) return "📅 Mid-afternoon — decent open rate";
    if (hour >= 17 && hour < 19) return "📅 End of day — consider sending tomorrow 9 AM";
    if (hour >= 19) return "📅 Evening — best to schedule for tomorrow 9 AM";
    return "📅 Best time: 9–11 AM (highest open rates)";
  };

  const sendMessage = useCallback(async (overrideText) => {
    const userText = (overrideText || input).trim();
    if (!userText || loading) return;
    const history = messages
      .filter(m => m.role === "user" || m.role === "assistant")
      .map(m => ({ role: m.role, content: typeof m.content === "string" ? m.content : "" }));
    setMessages(prev => [...prev, { role: "user", type: "text", content: userText, animate: false }]);
    setInput(""); setLoading(true);
    // Force scroll to bottom when user sends — always show their message
    userScrolledUp.current = false;
    setTimeout(() => scrollToBottom(true), 60);

    // Inject tone into the message so the AI generates the right style templates
    const toneInstructions = {
      "Professional": "Write in a formal, business-professional tone.",
      "Friendly":     "Write in a warm, conversational, friendly tone.",
      "Brief":        "Write very concise and short — get straight to the point.",
      "Formal":       "Write in a very formal, structured tone suitable for senior management.",
    };
    const toneNote = toneInstructions[activeTone] || "";
    const messageWithTone = toneNote
      ? `${userText}

[TONE INSTRUCTION: ${toneNote}]`
      : userText;

    try {
      const res = await fetch("/api/ai/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages:    [...history, { role: "user", content: messageWithTone }],
          userEmail:   user?.email       || "",
          userName:    user?.name        || "",
          accessToken: user?.accessToken || "",
          tonePreference: activeTone,
        }),
      });
      if (!res.ok) throw new Error("Server error: " + res.status);
      const data = await res.json();

      if (data.success && data.data) {
        const r = data.data;

        // Block raw ACTION: text
        if ((r.message || "").trim().startsWith("ACTION:")) {
          setMessages(prev => [...prev, { role: "assistant", type: "text", animate: true,
            content: "I ran into a hiccup. Could you rephrase your request?" }]);
          return;
        }

        if (r.type === "pdf_ready" && r.pdfBase64) {
          setMessages(prev => [...prev, { role: "assistant", type: "pdf", animate: true,
            content: r.message, pdfBase64: r.pdfBase64, pdfTitle: r.pdfTitle }]);

        } else if (r.type === "email_templates") {
          let parsedTemplates = [];
          try {
            // Deep parse — handle single or double encoded JSON
            let raw = r.templatesJson || "";
            let parsed = null;

            // Try parsing once
            try { parsed = JSON.parse(raw); } catch { parsed = null; }

            // If result is still a string, parse again (double encoded)
            if (typeof parsed === "string") {
              try { parsed = JSON.parse(parsed); } catch { parsed = null; }
            }

            // Extract templates array from wherever it lives
            if (parsed) {
              if (Array.isArray(parsed)) {
                parsedTemplates = parsed;
              } else if (Array.isArray(parsed.templates)) {
                parsedTemplates = parsed.templates;
              } else if (parsed.templates) {
                parsedTemplates = [parsed.templates];
              }
            }

            // Validate each template has proper fields
            parsedTemplates = parsedTemplates.map((t, i) => ({
              label:   t.label   || t.tone  || ["Professional","Friendly","Brief"][i] || "Email",
              subject: t.subject || t.Subject || "No Subject",
              body:    t.body    || t.Body    || t.content || "",
            })).filter(t => t.body && t.body.length > 5 && !t.body.startsWith("{"));

          } catch (e) {
            console.error("Template parse error:", e);
          }

          // If still empty or broken, show error message
          if (parsedTemplates.length === 0) {
            setMessages(prev => [...prev, { role: "assistant", type: "text", animate: true,
              content: "⚠️ Could not parse email templates. Please try again with a clearer request." }]);
            return;
          }

          // Ensure we always have 3 templates
          while (parsedTemplates.length < 3) {
            parsedTemplates.push({
              label:   ["Professional","Friendly","Brief"][parsedTemplates.length],
              subject: parsedTemplates[0]?.subject || "Email",
              body:    parsedTemplates[0]?.body    || "",
            });
          }
          setMessages(prev => [...prev, { role: "assistant", type: "email_templates", animate: false,
            content: r.message, templates: parsedTemplates, recipientEmail: r.recipientEmail || "" }]);

        } else if (r.type === "form_fields") {
          setMessages(prev => [...prev, { role: "assistant", type: "form_fields", animate: true,
            content: r.message, formUrl: r.formUrl }]);

        } else if (r.type === "form_submitted") {
          setMessages(prev => [...prev, { role: "assistant", type: "success", animate: true, content: r.message }]);

        } else if (r.type === "scraped_data") {
          setMessages(prev => [...prev, { role: "assistant", type: "scraped", animate: true, content: r.message }]);

        } else if (r.type === "image_generated") {
          setMessages(prev => [...prev, { role: "assistant", type: "image", animate: false,
            content: r.message, prompt: r.prompt }]);

        } else {
          setMessages(prev => [...prev, { role: "assistant", type: r.type || "text",
            content: r.message || "Done!", animate: true }]);
        }
      } else {
        setMessages(prev => [...prev, { role: "assistant", type: "error", animate: true,
          content: "⚠️ " + (data.message || "Something went wrong.") }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: "assistant", type: "error", animate: true, content: "⚠️ " + err.message }]);
    } finally { setLoading(false); }
  }, [input, loading, messages, user]);

  const downloadPdf = (b64, name) => {
    const a = document.createElement("a");
    a.href = "data:application/pdf;base64," + b64; a.download = name;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  // Save current chat to history before clearing
  const saveCurrentChatToHistory = (msgs) => {
    const userMsgs = (msgs || messages).filter(m => m.role === "user");
    if (userMsgs.length === 0) return; // don't save empty chats
    const firstUserMsg = userMsgs[0]?.content || "Chat";
    const title = firstUserMsg.length > 50 ? firstUserMsg.substring(0, 50) + "…" : firstUserMsg;
    const session = {
      id: Date.now(),
      title,
      savedAt: new Date().toISOString(),
      messages: (msgs || messages).slice(-100).map(m => ({ ...m, animate: false })),
    };
    setChatSessions(prev => {
      const updated = [session, ...prev].slice(0, 20); // keep last 20 chats
      try { localStorage.setItem("santharion_sessions", JSON.stringify(updated)); } catch(e) {}
      return updated;
    });
  };

  const loadChatSession = (session) => {
    setMessages(session.messages || []);
    setSidebarOpen(false);
  };

  const deleteChatSession = (id, e) => {
    e.stopPropagation();
    setChatSessions(prev => {
      const updated = prev.filter(s => s.id !== id);
      try { localStorage.setItem("santharion_sessions", JSON.stringify(updated)); } catch(e) {}
      return updated;
    });
  };

  const clearChat = () => {
    saveCurrentChatToHistory();
    localStorage.removeItem("santharion_chat");
    localStorage.removeItem("santharion_threads");
    localStorage.removeItem("santharion_sent");

    setTrackedThreads([]);
    setSentEmails([]);
    setMessages([{
      role: "assistant", type: "text", animate: false,
      content: "✨ New chat started! How can Santharion AI help you today?",
    }]);
  };

  // eslint-disable-next-line no-unused-vars
  const suggestions = [
    { icon: "📧", text: "Send a follow-up email to my client",       color: "#f97316", glow: "rgba(249,115,22,0.25)",   bg: "rgba(249,115,22,0.08)"  },
    { icon: "💼", text: "Write a professional email to my boss",      color: "#ec4899", glow: "rgba(236,72,153,0.25)",  bg: "rgba(236,72,153,0.08)"  },
    { icon: "🤝", text: "Email a thank you after the interview",      color: "#8b5cf6", glow: "rgba(139,92,246,0.25)",  bg: "rgba(139,92,246,0.08)"  },
    { icon: "⚠️", text: "Apologize for the project delay",            color: "#f59e0b", glow: "rgba(245,158,11,0.25)",  bg: "rgba(245,158,11,0.08)"  },
    { icon: "📋", text: "Send a meeting request to the team",         color: "#06b6d4", glow: "rgba(6,182,212,0.25)",   bg: "rgba(6,182,212,0.08)"   },
    { icon: "💬", text: "Write a friendly check-in to a colleague",   color: "#10b981", glow: "rgba(16,185,129,0.25)",  bg: "rgba(16,185,129,0.08)"  },
  ];

  // ── RENDER MESSAGE ────────────────────────────────────────────────
  const renderMsg = (msg, i) => {
    const isUser = msg.role === "user";

    // Email templates
    if (msg.type === "email_templates") {
      return (
        <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="flex justify-start items-start gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-1"
            style={{ background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.3)" }}>
            <SantharionLogo size={22}/>
          </div>
          <div className="flex-1 flex flex-col gap-4 min-w-0">
            <div className="px-4 py-3 rounded-2xl rounded-bl-none text-sm text-gray-200 self-start max-w-md"
              style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
              {renderFormatted(msg.content)}
            </div>
            <EmailTemplatesContainer templates={msg.templates || []} recipientEmail={msg.recipientEmail}
              onSendTemplate={sendEmailTemplate} userName={user?.name} userEmail={user?.email}
              accessToken={user?.accessToken}
              onSessionExpired={handleSessionExpired}
              onComposeOpen={(open) => setComposeModalOpen(open)}
              onScheduleEmail={(emailObj) => {
                setScheduledEmails(prev => [emailObj, ...prev]);
                setReplyAlert({ from:"📅 Email Scheduled!", subject:`"${emailObj.subject}" → ${emailObj.to} on ${new Date(emailObj.sendAt).toLocaleString("en-IN",{dateStyle:"medium",timeStyle:"short"})}`, threadId:null });
                setTimeout(() => setReplyAlert(null), 7000);
              }}/>
          </div>
        </motion.div>
      );
    }

    // Image
    if (msg.type === "image") {
      return (
        <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="flex justify-start items-start gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-1"
            style={{ background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.3)" }}>
            <SantharionLogo size={22}/>
          </div>
          <div className="flex-1 flex flex-col gap-3 min-w-0 max-w-md">
            <div className="px-4 py-3 rounded-2xl rounded-bl-none text-sm text-gray-200"
              style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
              {renderFormatted(msg.content)}
            </div>
            <ImageResultCard prompt={msg.prompt}/>
          </div>
        </motion.div>
      );
    }

    // Form fields card
    if (msg.type === "form_fields") {
      return (
        <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="flex justify-start items-start gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-1"
            style={{ background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.3)" }}>
            <SantharionLogo size={22}/>
          </div>
          <div className="flex-1 flex flex-col gap-3 min-w-0">
            <div className="px-4 py-3 rounded-2xl rounded-bl-none text-sm text-gray-200"
              style={{ background: "rgba(139,92,246,0.08)", border: "1px solid rgba(139,92,246,0.25)" }}>
              {renderFormatted(msg.content)}
            </div>
            {msg.formUrl && (
              <a href={msg.formUrl} target="_blank" rel="noopener noreferrer"
                className="self-start flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold"
                style={{ background: "rgba(139,92,246,0.1)", border: "1px solid rgba(139,92,246,0.3)", color: "#a78bfa" }}>
                <FaGlobe size={10}/> Open form in new tab
              </a>
            )}
          </div>
        </motion.div>
      );
    }

    // Scraped data
    if (msg.type === "scraped") {
      return (
        <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="flex justify-start items-start gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-1"
            style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.3)" }}>
            <SantharionLogo size={22}/>
          </div>
          <div className="px-4 py-3 rounded-2xl rounded-bl-none text-sm text-gray-200 max-w-2xl"
            style={{ background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.2)" }}>
            {msg.animate
              ? <Typewriter text={msg.content} onDone={() => setMessages(prev => prev.map((m,j) => j===i?{...m,animate:false}:m))}/>
              : renderFormatted(msg.content)}
          </div>
        </motion.div>
      );
    }

    // reply_notification — injected when a reply is detected
    if (msg.type === "reply_received" || msg.type === "reply_notification") {
      return (
        <motion.div key={i} initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
          className="flex justify-start items-start gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-1"
            style={{ background:"rgba(34,197,94,0.15)", border:"1px solid rgba(34,197,94,0.3)" }}>
            <SantharionLogo size={22}/>
          </div>
          <div className="px-4 py-4 rounded-2xl rounded-bl-none text-sm"
            style={{ background:"rgba(34,197,94,0.06)", border:"1px solid rgba(34,197,94,0.25)", maxWidth:"90%",
              whiteSpace:"pre-wrap", lineHeight:1.7, color:"#d1fae5" }}>
            {msg.content}
          </div>
        </motion.div>
      );
    }

    // ── Follow-up reminder ────────────────────────────────────────
    if (msg.type === "followup_reminder") {
      return (
        <motion.div key={i} initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
          className="flex justify-start items-start gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-1"
            style={{ background:"rgba(245,158,11,0.15)", border:"1px solid rgba(245,158,11,0.3)" }}>
            <SantharionLogo size={22}/>
          </div>
          <div className="flex-1 flex flex-col gap-3 min-w-0 max-w-lg">
            <div className="px-4 py-3 rounded-2xl rounded-bl-none text-sm"
              style={{ background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.3)", color:"#fde68a" }}>
              {renderFormatted(msg.content)}
            </div>
            <div className="flex gap-2 flex-wrap">
              {[1,2,3].map(days => (
                <motion.button key={days} whileHover={{ scale:1.03 }} whileTap={{ scale:0.97 }}
                  onClick={() => sendMessage(`Write a follow-up email to ${msg.followUpData?.to} about "${msg.followUpData?.subject}" after ${days} day${days>1?"s":""} of no reply`)}
                  className="px-3 py-1.5 rounded-xl text-xs font-bold"
                  style={{ background:"rgba(245,158,11,0.15)", border:"1px solid rgba(245,158,11,0.3)", color:"#fbbf24" }}>
                  Write follow-up
                </motion.button>
              ))}
              <motion.button whileHover={{ scale:1.03 }} whileTap={{ scale:0.97 }}
                onClick={() => setMessages(prev => prev.filter((_, j) => j !== i))}
                className="px-3 py-1.5 rounded-xl text-xs font-bold"
                style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.1)", color:"#6b7280" }}>
                Dismiss
              </motion.button>
            </div>
          </div>
        </motion.div>
      );
    }

    // ── Success with follow-up options — clean version ───────────
    if (msg.type === "success_with_options") {
      const sc = msg.score || emailScore;
      return (
        <motion.div key={i} initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
          className="flex justify-start items-start gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-1"
            style={{ background:"rgba(34,197,94,0.15)", border:"1px solid rgba(34,197,94,0.3)" }}>
            <SantharionLogo size={22}/>
          </div>
          <div className="flex-1 flex flex-col gap-2 min-w-0 max-w-lg">
            {/* Clean success card */}
            <div className="px-4 py-3 rounded-2xl rounded-bl-none"
              style={{ background:"rgba(34,197,94,0.08)", border:"1px solid rgba(34,197,94,0.25)" }}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-base">✅</span>
                <span className="text-sm font-bold" style={{ color:"#4ade80" }}>Email sent!</span>
              </div>
              <p className="text-xs" style={{ color:"#6b7280" }}>
                To: <span style={{ color:"#86efac" }}>{msg.to}</span>
              </p>
              <p className="text-xs mt-0.5 flex items-center gap-1" style={{ color:"#4b5563" }}>
                <span>🔄</span> Watching for replies every 30 mins
              </p>
              {/* Smart scheduler hint */}
              {scheduleHint && (
                <p className="text-xs mt-1.5 flex items-center gap-1" style={{ color:"#fbbf24" }}>
                  <span>📅</span> {scheduleHint}
                </p>
              )}
            </div>
            {/* Email Quality Score */}
            {sc && (
              <div style={{ padding:"12px 14px", borderRadius:16, background:"rgba(139,92,246,0.07)", border:"1px solid rgba(139,92,246,0.2)" }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                  <span style={{ fontSize:11, fontWeight:700, color:"#a78bfa" }}>📊 Email Quality Score</span>
                  <span style={{ fontSize:18, fontWeight:800, background:"linear-gradient(135deg,#f97316,#ec4899)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>{sc.overall}</span>
                </div>
                {[["Clarity",sc.clarity,"#4ade80"],["Tone",sc.tone,"#f97316"],["Urgency",sc.urgency,"#ec4899"]].map(([label,val,col]) => (
                  <div key={label} style={{ marginBottom:5 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:"#4b5563", marginBottom:3 }}>
                      <span>{label}</span><span style={{ color:col }}>{val}%</span>
                    </div>
                    <div style={{ height:4, borderRadius:999, background:"rgba(255,255,255,0.06)", overflow:"hidden" }}>
                      <div className="score-bar-fill" style={{ height:"100%", borderRadius:999, background:`linear-gradient(90deg,${col},${col}99)`, "--w":`${val}%`, width:`${val}%` }}/>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Follow-up row */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold" style={{ color:"#6b7280" }}>⏰ Remind me:</span>
              {[
                { days:1, label:"Tomorrow" },
                { days:3, label:"3 days" },
                { days:7, label:"1 week" },
                { days:0, label:"Skip" },
              ].map(opt => (
                <motion.button key={opt.days} whileHover={{ scale:1.04 }} whileTap={{ scale:0.96 }}
                  onClick={() => {
                    if (opt.days > 0) {
                      const remindAt = Date.now() + opt.days * 24*60*60*1000;
                      setFollowUps(prev => [...prev, {
                        to: msg.to, subject: msg.subject, threadId: msg.threadId,
                        remindAt, label: opt.label.toLowerCase()
                      }]);
                      setMessages(prev => prev.map((m,j) => j===i
                        ? {...m, type:"success", content:`✅ Email sent! Reminder set for ${opt.label}.`} : m));
                    } else {
                      setMessages(prev => prev.map((m,j) => j===i
                        ? {...m, type:"success", content:"✅ Email sent!"} : m));
                    }
                  }}
                  className="px-3 py-1 rounded-xl text-xs font-bold transition"
                  style={{
                    background: opt.days === 0 ? "rgba(255,255,255,0.04)" : "rgba(34,197,94,0.12)",
                    border: opt.days === 0 ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(34,197,94,0.3)",
                    color: opt.days === 0 ? "#4b5563" : "#4ade80"
                  }}>
                  {opt.label}
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>
      );
    }

    // Standard bubble
    const bg = isUser
      ? "linear-gradient(135deg,#f97316,#ec4899)"
      : msg.type === "error"      ? undefined
      : msg.type === "success"    ? undefined
      : msg.type === "need_login" ? undefined
      : undefined;

    const bubbleStyle = isUser
      ? { background: bg, color: "#fff", borderRadius: "1.5rem 1.5rem 0.25rem 1.5rem" }
      : msg.type === "error"      ? { background: "rgba(239,68,68,0.08)",   border: "1px solid rgba(239,68,68,0.3)",   color: "#fca5a5", borderRadius: "1.5rem 1.5rem 1.5rem 0.25rem" }
      : msg.type === "success"    ? { background: "rgba(34,197,94,0.08)",   border: "1px solid rgba(34,197,94,0.3)",   color: "#86efac", borderRadius: "1.5rem 1.5rem 1.5rem 0.25rem" }
      : msg.type === "need_login" ? { background: "rgba(249,115,22,0.08)",  border: "1px solid rgba(249,115,22,0.3)",  color: "#fdba74", borderRadius: "1.5rem 1.5rem 1.5rem 0.25rem" }
      : { background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)", color: "#e5e7eb", borderRadius: "1.5rem 1.5rem 1.5rem 0.25rem" };

    return (
      <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
        className={`flex ${isUser ? "justify-end" : "justify-start"} items-end gap-2.5`}>
        {!isUser && (
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(249,115,22,0.1)", border: "1px solid rgba(249,115,22,0.3)" }}>
            <SantharionLogo size={22}/>
          </div>
        )}
        <div className="max-w-[78%] flex flex-col gap-2">
          <div className="px-4 py-3.5 text-sm leading-relaxed shadow-lg" style={bubbleStyle}>
            {!isUser && msg.animate
              ? <Typewriter text={msg.content} onDone={() => setMessages(prev => prev.map((m,j) => j===i?{...m,animate:false}:m))}/>
              : renderFormatted(msg.content)}
          </div>
          {msg.type === "error" && msg.content && (msg.content.includes("permission expired") || msg.content.includes("Gmail permission")) && (
            <motion.button initial={{ opacity:0 }} animate={{ opacity:1 }}
              onClick={handleSignOut}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-xs font-bold hover:scale-105 transition self-start"
              style={{ background:"linear-gradient(135deg,#f97316,#ec4899)", boxShadow:"0 4px 14px rgba(249,115,22,0.3)" }}>
              🔄 Sign Out & Sign In Again
            </motion.button>
          )}
          {msg.type === "pdf" && msg.pdfBase64 && (
            <motion.button initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              onClick={() => downloadPdf(msg.pdfBase64, msg.pdfTitle)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-bold hover:scale-105 transition shadow-lg self-start"
              style={{ background: "linear-gradient(135deg,#8b5cf6,#ec4899)" }}>
              <FaFilePdf/> Download: {msg.pdfTitle}
            </motion.button>
          )}
          {msg.type === "need_login" && !user && (
            <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              onClick={() => {
                // Save the recent conversation so we can restore after OAuth redirect
                const history = messages
                  .filter(m => m.role === "user" || m.role === "assistant")
                  .slice(-10)
                  .map(m => ({ role: m.role, content: typeof m.content === "string" ? m.content : "" }));
                const lastUserMsg = [...messages].reverse().find(m => m.role === "user");
                if (lastUserMsg) {
                  sessionStorage.setItem("santharion_pending_email", JSON.stringify({
                    originalMessage: lastUserMsg.content,
                    history: history,
                  }));
                }
                handleSignIn();
              }}
              className="flex items-center gap-3 px-5 py-3 bg-white text-gray-800 rounded-2xl font-bold shadow-xl hover:scale-105 transition text-sm self-start">
              <GoogleIcon/> Sign in with Google
            </motion.button>
          )}
        </div>
        {isUser && (
          <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0 overflow-hidden"
            style={{ background: "linear-gradient(135deg,#f97316,#ec4899)", boxShadow:"0 2px 8px rgba(249,115,22,0.4)" }}>
            {user?.picture
              ? <img src={user.picture} alt="" className="w-full h-full object-cover rounded-xl"/>
              : <span className="text-base">{getAvatarEmoji(user?.name)}</span>}
          </div>
        )}
      </motion.div>
    );
  };

  // ── MAIN RENDER ────────────────────────────────────────────────────
  return (
    <div className="flex text-white overflow-hidden"
      style={{
        background:"#080410",
        fontFamily:"'Sora',sans-serif",
        height: isMobile ? "100dvh" : "100vh",
        position: isMobile ? "fixed" : "relative",
        top: isMobile ? 0 : "auto",
        left: isMobile ? 0 : "auto",
        right: isMobile ? 0 : "auto",
        bottom: isMobile ? 0 : "auto",
        width: "100%",
        zIndex: isMobile ? 9999 : "auto",
      }}>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800;900&display=swap');
        .grad-text-ai { background: linear-gradient(135deg,#f97316,#ec4899,#8b5cf6); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }
        * { font-family: 'Sora', 'Segoe UI Emoji', 'Apple Color Emoji', 'Noto Color Emoji', sans-serif; }
        .chat-area::-webkit-scrollbar { width: 4px; }
        .chat-area::-webkit-scrollbar-thumb { background: rgba(249,115,22,0.2); border-radius: 2px; }
        @keyframes shimmer { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }
        @keyframes pulse-glow { 0%,100%{box-shadow:0 0 0px rgba(249,115,22,0)} 50%{box-shadow:0 0 20px rgba(249,115,22,0.4)} }
        @media (max-width: 768px) {
          .chat-area { padding-bottom: 80px !important; }
        }
        .input-glow:focus-within { box-shadow: 0 0 0 2px rgba(249,115,22,0.5), 0 4px 40px rgba(249,115,22,0.18); border-color: rgba(249,115,22,0.5) !important; }
        .input-glow { transition: all 0.3s ease; }
        .suggestion-btn:active { transform: scale(0.96); }
        @keyframes shimmer { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
        @keyframes scoreIn { from{width:0} to{width:var(--w)} }
        .score-bar-fill { animation: scoreIn 1s ease forwards; }
        .tone-btn { transition: all 0.18s; border: 1px solid transparent; }
        .tone-btn:hover { transform: translateY(-1px); }
        .tone-btn.active { transform: scale(1.04); }
        .insight-card { transition: all 0.2s; }
        .insight-card:hover { transform: translateX(3px); }
        .nav-feature-btn { transition: all 0.18s; }
        .nav-feature-btn:hover { background: rgba(255,255,255,0.05) !important; color: #e5e7eb !important; }
        .welcome-card { background: linear-gradient(135deg, rgba(139,92,246,0.06), rgba(249,115,22,0.04)); border: 1px solid rgba(255,255,255,0.07); border-radius: 20px; padding: 28px 24px; text-align: center; margin-bottom: 8px; }
        .sug-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px; }
        .sug-card { display:flex; align-items:flex-start; gap:10px; padding:11px 13px; border-radius:14px; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.07); cursor:pointer; transition:all 0.2s; }
        .sug-card:hover { background:rgba(255,255,255,0.07); border-color:rgba(249,115,22,0.3); transform:translateY(-2px); }
        .insights-panel { width:272px; flex-shrink:0; display:flex; flex-direction:column; height:100%; background:linear-gradient(180deg,#07031a,#050210); border-left:1px solid rgba(255,255,255,0.07); overflow-y:auto; z-index:10; position:relative; }
        .insights-panel::-webkit-scrollbar { width: 3px; }
        .insights-panel::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.05); border-radius:2px; }
        .onboarding-overlay { position:fixed; inset:0; z-index:9999; background:rgba(0,0,0,0.88); display:flex; align-items:center; justify-content:center; backdrop-filter:blur(12px); padding:16px; }
        .onboarding-card { background:linear-gradient(160deg,#0f0820,#080415,#0a0418); border:1px solid rgba(139,92,246,0.25); border-radius:28px; max-width:520px; width:100%; max-height:90vh; display:flex; flex-direction:column; overflow:hidden; box-shadow:0 40px 120px rgba(0,0,0,0.8), 0 0 60px rgba(139,92,246,0.1); }
        @keyframes featureReveal { from{opacity:0;transform:translateY(16px) scale(0.96)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes stepGlow { 0%,100%{box-shadow:0 0 0 0 rgba(249,115,22,0)} 50%{box-shadow:0 0 20px 4px rgba(249,115,22,0.2)} }
        @keyframes progressFill { from{width:0%} to{width:100%} }
        @keyframes schedShake {
          0%,100% { transform: translateX(0); }
          20%     { transform: translateX(-7px); }
          40%     { transform: translateX(7px); }
          60%     { transform: translateX(-4px); }
          80%     { transform: translateX(4px); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* ── ONBOARDING OVERLAY — multi-step animated ── */}
      {showOnboarding && (() => {
        const features = [
          { icon:"✉️", title:"AI Email Writer",      desc:"3 templates — Pro, Friendly, Brief",       color:"#f97316" },
          { icon:"🎛",  title:"Tone Switcher",        desc:"Pick tone before generating",               color:"#ec4899" },
          { icon:"📊", title:"Email Quality Score",  desc:"Scores Clarity, Tone & Urgency 0-100",     color:"#fbbf24" },
          { icon:"📬", title:"Reply Detection",      desc:"Alerts when someone replies",               color:"#4ade80" },
          { icon:"🧠", title:"Email Memory",         desc:"Remembers contacts you email often",        color:"#8b5cf6" },
          { icon:"🌡", title:"Reply Mood Detector",  desc:"Positive / Negative / Urgent on replies",  color:"#ec4899" },
          { icon:"📅", title:"Smart Scheduler",      desc:"Best send time by timezone",               color:"#22d3ee" },
          { icon:"⏰", title:"Follow-up Reminders",  desc:"Remind if no reply in 1/3/7 days",         color:"#fbbf24" },
          { icon:"🎤", title:"Voice Input",          desc:"Speak instead of typing",                   color:"#a78bfa" },
          { icon:"📎", title:"File Attachments",     desc:"Attach up to 5 files",                     color:"#f97316" },
          { icon:"🌐", title:"Indian Languages",     desc:"Hindi, Tamil, Telugu & more",              color:"#22d3ee" },
        ];
        const steps = ["welcome","howto","features","ready"];
        const totalSteps = steps.length;
        const finishOnboarding = () => { setShowOnboarding(false); setOnboardingStep(0); try{localStorage.setItem("santharion_onboarded","1")}catch(e){} };
        return (
          <div className="onboarding-overlay">
            <motion.div initial={{ opacity:0, scale:0.92, y:24 }} animate={{ opacity:1, scale:1, y:0 }}
              transition={{ type:"spring", stiffness:300, damping:28 }}
              onClick={e => e.stopPropagation()} className="onboarding-card">

              {/* Progress bar */}
              <div style={{ height:3, background:"rgba(255,255,255,0.06)", flexShrink:0 }}>
                <motion.div
                  animate={{ width:`${((onboardingStep+1)/totalSteps)*100}%` }}
                  transition={{ duration:0.5, ease:"easeOut" }}
                  style={{ height:"100%", background:"linear-gradient(90deg,#f97316,#ec4899,#8b5cf6)", borderRadius:999 }}/>
              </div>

              {/* Step dots */}
              <div style={{ display:"flex", justifyContent:"center", gap:6, padding:"12px 0 0", flexShrink:0 }}>
                {steps.map((_,i) => (
                  <div key={i} style={{ width: i===onboardingStep?20:6, height:6, borderRadius:999, transition:"all 0.3s",
                    background: i<=onboardingStep ? "linear-gradient(90deg,#f97316,#ec4899)" : "rgba(255,255,255,0.1)" }}/>
                ))}
              </div>

              {/* Scrollable content */}
              <div style={{ flex:1, overflowY:"auto", padding:"20px 24px 0", scrollbarWidth:"none" }}>

                {/* STEP 0 — Welcome */}
                {onboardingStep === 0 && (
                  <motion.div initial={{ opacity:0, x:30 }} animate={{ opacity:1, x:0 }} transition={{ duration:0.35 }}
                    style={{ textAlign:"center", paddingBottom:8 }}>
                    <motion.div animate={{ rotate:[0,8,-8,0], scale:[1,1.08,1] }} transition={{ repeat:Infinity, duration:5, ease:"easeInOut" }}
                      style={{ display:"inline-block", marginBottom:16 }}>
                      <SantharionLogo size={68}/>
                    </motion.div>
                    <h2 style={{ fontSize:24, fontWeight:900, marginBottom:10, letterSpacing:"-0.5px",
                      background:"linear-gradient(135deg,#f97316,#ec4899,#8b5cf6)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>
                      Welcome to Santharion AI 🚀
                    </h2>
                    <p style={{ fontSize:13, color:"#64748b", lineHeight:1.7, maxWidth:340, margin:"0 auto 24px" }}>
                      The smartest free AI email assistant. I write & send professional emails from your Gmail in seconds.
                    </p>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, textAlign:"left" }}>
                      {[
                        { icon:"⚡", text:"Emails in seconds, not minutes" },
                        { icon:"🆓", text:"100% free, no subscription" },
                        { icon:"📧", text:"Sends directly from your Gmail" },
                        { icon:"🔒", text:"No password stored ever" },
                      ].map((p,i) => (
                        <motion.div key={i} initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.1+i*0.08 }}
                          style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 12px",
                            borderRadius:12, background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.07)" }}>
                          <span style={{ fontSize:18 }}>{p.icon}</span>
                          <span style={{ fontSize:12, fontWeight:600, color:"#d1d5db" }}>{p.text}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* STEP 1 — How to use */}
                {onboardingStep === 1 && (
                  <motion.div initial={{ opacity:0, x:30 }} animate={{ opacity:1, x:0 }} transition={{ duration:0.35 }}
                    style={{ paddingBottom:8 }}>
                    <h3 style={{ fontSize:18, fontWeight:800, textAlign:"center", marginBottom:6, color:"#f1f5f9" }}>How it works</h3>
                    <p style={{ fontSize:12, color:"#4b5563", textAlign:"center", marginBottom:20, lineHeight:1.6 }}>3 simple steps to send any email</p>
                    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                      {[
                        { num:"1", icon:"💬", title:"Describe your email", desc:"Just type what you want to say and who to send it to. No need to write the email yourself.", color:"#f97316" },
                        { num:"2", icon:"⚡", title:"Pick your template", desc:"I'll generate 3 versions — Professional, Friendly, and Brief. Click the one you like.", color:"#ec4899" },
                        { num:"3", icon:"📤", title:"Click send!", desc:"One click and your email goes from your actual Gmail account. No copy-paste needed.", color:"#8b5cf6" },
                      ].map((s,i) => (
                        <motion.div key={i} initial={{ opacity:0, x:20 }} animate={{ opacity:1, x:0 }} transition={{ delay:i*0.12 }}
                          style={{ display:"flex", gap:14, alignItems:"flex-start", padding:"14px 16px",
                            borderRadius:16, background:`${s.color}08`, border:`1px solid ${s.color}20` }}>
                          <div style={{ width:36, height:36, borderRadius:12, background:`${s.color}20`, border:`1px solid ${s.color}40`,
                            display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>
                            {s.icon}
                          </div>
                          <div>
                            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                              <span style={{ fontSize:10, fontWeight:800, color:s.color, background:`${s.color}18`, padding:"2px 7px", borderRadius:999 }}>Step {s.num}</span>
                            </div>
                            <div style={{ fontSize:13, fontWeight:700, color:"#e2e8f0", marginBottom:4 }}>{s.title}</div>
                            <div style={{ fontSize:11, color:"#64748b", lineHeight:1.5 }}>{s.desc}</div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* STEP 2 — Features grid */}
                {onboardingStep === 2 && (
                  <motion.div initial={{ opacity:0, x:30 }} animate={{ opacity:1, x:0 }} transition={{ duration:0.35 }}
                    style={{ paddingBottom:8 }}>
                    <h3 style={{ fontSize:18, fontWeight:800, textAlign:"center", marginBottom:4, color:"#f1f5f9" }}>All Features</h3>
                    <p style={{ fontSize:12, color:"#4b5563", textAlign:"center", marginBottom:16, lineHeight:1.6 }}>Everything packed into one free app</p>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                      {features.map((f,i) => (
                        <motion.div key={i}
                          initial={{ opacity:0, scale:0.85, y:12 }}
                          animate={{ opacity:1, scale:1, y:0 }}
                          transition={{ delay:i*0.06, type:"spring", stiffness:300, damping:24 }}
                          style={{ display:"flex", alignItems:"flex-start", gap:9, padding:"10px 11px",
                            borderRadius:14, background:`${f.color}08`, border:`1px solid ${f.color}18`,
                            cursor:"default" }}>
                          <div style={{ width:30, height:30, borderRadius:9, background:`${f.color}18`,
                            display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, flexShrink:0 }}>
                            {f.icon}
                          </div>
                          <div style={{ minWidth:0 }}>
                            <div style={{ fontSize:11, fontWeight:700, color:"#e2e8f0", lineHeight:1.3 }}>{f.title}</div>
                            <div style={{ fontSize:9, color:"#4b5563", marginTop:2, lineHeight:1.4 }}>{f.desc}</div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* STEP 3 — Ready */}
                {onboardingStep === 3 && (
                  <motion.div initial={{ opacity:0, scale:0.9 }} animate={{ opacity:1, scale:1 }} transition={{ duration:0.4, type:"spring" }}
                    style={{ textAlign:"center", paddingBottom:8 }}>
                    <motion.div animate={{ scale:[1,1.15,1], rotate:[0,10,-10,0] }} transition={{ duration:1.2, repeat:2 }}
                      style={{ fontSize:64, marginBottom:16, display:"block" }}>🚀</motion.div>
                    <h3 style={{ fontSize:22, fontWeight:900, marginBottom:10, color:"#f1f5f9", letterSpacing:"-0.5px" }}>You're all set!</h3>
                    <p style={{ fontSize:13, color:"#64748b", lineHeight:1.7, marginBottom:24, maxWidth:300, margin:"0 auto 24px" }}>
                      Sign in with Google and send your first AI-powered email in under 30 seconds.
                    </p>
                    <div style={{ padding:"16px", borderRadius:16, background:"rgba(249,115,22,0.06)", border:"1px solid rgba(249,115,22,0.2)", marginBottom:8, textAlign:"left" }}>
                      <div style={{ fontSize:11, fontWeight:700, color:"#fb923c", marginBottom:6 }}>💡 Pro tip for first time</div>
                      <div style={{ fontSize:11, color:"#94a3b8", lineHeight:1.6 }}>
                        Type something like: <span style={{ color:"#e2e8f0", fontStyle:"italic" }}>"Send a follow-up email to my client about the project"</span> — and watch me write 3 perfect emails instantly.
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Footer buttons — fixed at bottom */}
              <div style={{ padding:"16px 24px 20px", flexShrink:0, borderTop:"1px solid rgba(255,255,255,0.06)", display:"flex", gap:10, alignItems:"center" }}>
                {onboardingStep > 0 && (
                  <button onClick={() => setOnboardingStep(s => s-1)}
                    style={{ padding:"11px 18px", borderRadius:12, border:"1px solid rgba(255,255,255,0.1)",
                      background:"rgba(255,255,255,0.04)", color:"#6b7280", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
                    ← Back
                  </button>
                )}
                <button
                  onClick={() => {
                    if (onboardingStep < totalSteps-1) setOnboardingStep(s => s+1);
                    else finishOnboarding();
                  }}
                  style={{ flex:1, padding:"13px", borderRadius:14, border:"none", cursor:"pointer", fontFamily:"inherit",
                    background:"linear-gradient(135deg,#f97316,#ec4899)", color:"#fff", fontSize:14, fontWeight:800,
                    boxShadow:"0 4px 24px rgba(249,115,22,0.4)", transition:"transform 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.transform="scale(1.02)"}
                  onMouseLeave={e => e.currentTarget.style.transform="scale(1)"}>
                  {onboardingStep === 0 ? "Let's See How It Works →" :
                   onboardingStep === 1 ? "See All Features →" :
                   onboardingStep === 2 ? "I'm Ready! →" :
                   "🚀 Let's Go!"}
                </button>
                {onboardingStep === 0 && (
                  <button onClick={finishOnboarding}
                    style={{ padding:"11px 14px", borderRadius:12, border:"1px solid rgba(255,255,255,0.08)",
                      background:"none", color:"#374151", fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                    Skip
                  </button>
                )}
              </div>

            </motion.div>
          </div>
        );
      })()}

      {/* Ambient orbs */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full opacity-[0.06] blur-[120px]"
          style={{ background:"radial-gradient(circle,#f97316,#ec4899)" }}/>
        <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full opacity-[0.05] blur-[120px]"
          style={{ background:"radial-gradient(circle,#8b5cf6,#3b82f6)" }}/>
      </div>

      {/* ══════════════════════════════════════════════════════════
           LEFT SIDEBAR — desktop only, mobile uses slide-in overlay
      ═══════════════════════════════════════════════════════════ */}
      {/* Desktop sidebar — hidden on mobile */}
      {!isMobile && (
      <div className="flex-shrink-0 flex flex-col h-full"
        style={{ width:"260px", background:"#0a0418", borderRight:"1px solid rgba(255,255,255,0.06)" }}>

        {/* Brand */}
        <div className="flex items-center gap-3 px-4 py-4"
          style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
          <motion.div animate={{ rotate:[0,5,-5,0] }} transition={{ repeat:Infinity, duration:6 }}>
            <SantharionLogo size={34}/>
          </motion.div>
          <div>
            <h1 className="text-sm font-black leading-none grad-text-ai">Santharion AI</h1>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"/>
              <span className="text-xs" style={{ color:"#22c55e" }}>Online · Free</span>
            </div>
          </div>
        </div>

        {/* User profile */}
        <div className="px-3 py-3" style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
          {user ? (
            <motion.div initial={{ opacity:0, y:-6 }} animate={{ opacity:1, y:0 }}
              className="flex items-center gap-2.5 p-2.5 rounded-2xl"
              style={{ background:"linear-gradient(135deg,rgba(34,197,94,0.08),rgba(16,185,129,0.04))", border:"1px solid rgba(34,197,94,0.2)" }}>
              {/* Profile picture with fallback */}
              <div className="relative flex-shrink-0">
                {user.picture ? (
                  <img src={user.picture} alt={user.name}
                    referrerPolicy="no-referrer"
                    className="w-9 h-9 rounded-full object-cover"
                    onError={e => { e.target.style.display="none"; e.target.nextSibling.style.display="flex"; }}
                  />
                ) : null}
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-black flex-shrink-0"
                  style={{ background:"linear-gradient(135deg,#f97316,#ec4899)", color:"#fff",
                    display: user.picture ? "none" : "flex" }}>
                  {(user.name||"U")[0].toUpperCase()}
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-[#0a0418]"
                  style={{ background:"#22c55e" }}/>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold truncate" style={{ color:"#e5e7eb" }}>{user.name}</p>
                <p className="text-xs truncate" style={{ color:"#4ade80", fontSize:"10px" }}>● Connected</p>
              </div>
              <button onClick={handleSignOut} title="Sign out"
                className="p-1.5 rounded-lg transition flex-shrink-0"
                style={{ color:"#4b5563" }}
                onMouseEnter={e => e.currentTarget.style.color="#f87171"}
                onMouseLeave={e => e.currentTarget.style.color="#4b5563"}>
                <FaSignOutAlt size={11}/>
              </button>
            </motion.div>
          ) : (
            <motion.button onClick={handleSignIn}
              whileHover={{ scale:1.02 }} whileTap={{ scale:0.97 }}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-sm text-white"
              style={{ background:"linear-gradient(135deg,#f97316,#ec4899)", animation:"pulse-glow 2s ease infinite" }}>
              <GoogleIcon/> Sign in with Google
            </motion.button>
          )}
        </div>

        {/* Sidebar nav items */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1">

          {/* New Chat */}
          <button onClick={clearChat}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition text-left"
            style={{ background:"rgba(249,115,22,0.1)", border:"1px solid rgba(249,115,22,0.2)", color:"#fb923c" }}>
            <span style={{ fontSize:"16px" }}>✏️</span> New Chat
          </button>

          <div className="pt-2 pb-1">
            <p className="text-xs font-semibold px-2 mb-1" style={{ color:"#374151" }}>TONE</p>
          </div>

          {/* Tone Switcher */}
          <div style={{ display:"flex", gap:5, flexWrap:"wrap", padding:"2px 4px 6px" }}>
            {["Professional","Friendly","Brief","Formal"].map(tone => (
              <button key={tone} onClick={() => setActiveTone(tone)}
                className={"tone-btn " + (activeTone === tone ? "active" : "")}
                style={{
                  padding:"4px 10px", borderRadius:999, fontSize:11, fontWeight:700, cursor:"pointer",
                  fontFamily:"inherit",
                  background: activeTone === tone ? "linear-gradient(135deg,rgba(249,115,22,0.2),rgba(236,72,153,0.15))" : "rgba(255,255,255,0.04)",
                  border: activeTone === tone ? "1px solid rgba(249,115,22,0.4)" : "1px solid rgba(255,255,255,0.08)",
                  color: activeTone === tone ? "#fb923c" : "#4b5563",
                }}>
                {tone === "Professional" ? "🏢" : tone === "Friendly" ? "😊" : tone === "Brief" ? "⚡" : "🤝"} {tone}
              </button>
            ))}
          </div>

          {/* ══════════════════════════════════════
               MAIN ACTION TABS — LARGE & GLOWING
          ══════════════════════════════════════ */}
          <div style={{ padding:"12px 4px 4px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:10, paddingLeft:2 }}>
              <div style={{ width:3, height:14, borderRadius:999, background:"linear-gradient(#f97316,#ec4899)" }}/>
              <span style={{ fontSize:10, fontWeight:900, color:"#9ca3af", letterSpacing:".12em", textTransform:"uppercase" }}>Your Mailbox</span>
            </div>

            {/* ── SENT EMAILS ── */}
            <motion.button whileHover={{ scale:1.02, x:3 }} whileTap={{ scale:0.97 }}
              onClick={() => setShowSent(true)}
              style={{ width:"100%", marginBottom:8, padding:"14px 16px", borderRadius:16,
                background:"linear-gradient(135deg,rgba(34,197,94,0.1),rgba(16,185,129,0.06))",
                border:"1.5px solid rgba(34,197,94,0.22)",
                cursor:"pointer", display:"flex", alignItems:"center", gap:14,
                fontFamily:"inherit", textAlign:"left",
                boxShadow:"inset 0 1px 0 rgba(255,255,255,0.04)" }}>
              <div style={{ width:44, height:44, borderRadius:13, flexShrink:0,
                background:"linear-gradient(135deg,#22c55e,#16a34a)",
                boxShadow:"0 4px 14px rgba(34,197,94,0.35)",
                display:"flex", alignItems:"center", justifyContent:"center", fontSize:22 }}>📤</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:15, fontWeight:900, color:"#fff", letterSpacing:"-0.02em" }}>Sent Emails</div>
                <div style={{ fontSize:11, color:"#6b7280", marginTop:2 }}>
                  {sentEmails.length > 0 ? `${sentEmails.length} email${sentEmails.length>1?"s":""} sent` : "Emails you've sent"}
                </div>
              </div>
              {sentEmails.length > 0 ? (
                <span style={{ background:"rgba(34,197,94,0.2)", color:"#4ade80", borderRadius:999,
                  padding:"4px 11px", fontSize:13, fontWeight:900, border:"1px solid rgba(34,197,94,0.3)" }}>
                  {sentEmails.length}
                </span>
              ) : <span style={{ fontSize:18, opacity:0.4 }}>›</span>}
            </motion.button>

            {/* ── SCHEDULED EMAILS ── */}
            {(() => {
              const pending = scheduledEmails.filter(e=>!e.sent&&!e.failed).length;
              const hasPending = pending > 0;
              return (
                <motion.button whileHover={{ scale:1.02, x:3 }} whileTap={{ scale:0.97 }}
                  onClick={() => setShowScheduled(true)}
                  style={{ width:"100%", marginBottom:4, padding:"14px 16px", borderRadius:16,
                    background: hasPending
                      ? "linear-gradient(135deg,rgba(124,58,237,0.22),rgba(168,85,247,0.14))"
                      : "linear-gradient(135deg,rgba(124,58,237,0.1),rgba(168,85,247,0.06))",
                    border: hasPending ? "2px solid rgba(124,58,237,0.5)" : "1.5px solid rgba(124,58,237,0.2)",
                    cursor:"pointer", display:"flex", alignItems:"center", gap:14,
                    fontFamily:"inherit", textAlign:"left",
                    boxShadow: hasPending ? "0 0 24px rgba(124,58,237,0.2), inset 0 1px 0 rgba(255,255,255,0.05)" : "inset 0 1px 0 rgba(255,255,255,0.04)" }}>
                  <div style={{ width:44, height:44, borderRadius:13, flexShrink:0,
                    background:"linear-gradient(135deg,#7c3aed,#a855f7)",
                    boxShadow: hasPending ? "0 6px 20px rgba(124,58,237,0.5)" : "0 4px 14px rgba(124,58,237,0.3)",
                    display:"flex", alignItems:"center", justifyContent:"center", fontSize:22 }}>📅</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:15, fontWeight:900, color:"#fff", letterSpacing:"-0.02em" }}>Scheduled</div>
                    <div style={{ fontSize:11, color: hasPending ? "#c4b5fd" : "#6b7280", marginTop:2 }}>
                      {hasPending ? `${pending} queued to send` : "No emails queued"}
                    </div>
                  </div>
                  {hasPending ? (
                    <span style={{ background:"rgba(124,58,237,0.3)", color:"#c4b5fd", borderRadius:999,
                      padding:"4px 11px", fontSize:13, fontWeight:900, border:"1px solid rgba(124,58,237,0.4)" }}>
                      {pending}
                    </span>
                  ) : <span style={{ fontSize:18, opacity:0.4 }}>›</span>}
                </motion.button>
              );
            })()}
          </div>

          {/* ═══ AUTO-FEATURES — compact info strip ═══ */}
          <div style={{ margin:"8px 4px 4px", padding:"10px 14px", borderRadius:14,
            background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)" }}>
            <div style={{ fontSize:9, fontWeight:800, color:"#4b5563", letterSpacing:".12em",
              textTransform:"uppercase", marginBottom:8 }}>⚡ Always On</div>
            <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
              {/* Follow-up */}
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:15 }}>⏰</span>
                <div style={{ flex:1 }}>
                  <span style={{ fontSize:11, fontWeight:700, color: followUps.length>0 ? "#fbbf24" : "#6b7280" }}>
                    Follow-up Reminders
                  </span>
                  <span style={{ fontSize:10, color:"#374151", marginLeft:6 }}>
                    {followUps.length > 0 ? `${followUps.length} active` : "auto"}
                  </span>
                </div>
                {followUps.length > 0 && (
                  <motion.span animate={{ opacity:[1,0.5,1] }} transition={{ repeat:Infinity, duration:2 }}
                    style={{ width:8, height:8, borderRadius:999, background:"#f59e0b",
                      boxShadow:"0 0 8px #f59e0b", flexShrink:0 }}/>
                )}
              </div>
              {/* Style Learning */}
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:15 }}>🧠</span>
                <div style={{ flex:1 }}>
                  <span style={{ fontSize:11, fontWeight:700, color:"#a78bfa" }}>Style Learning</span>
                  <span style={{ fontSize:10, color:"#374151", marginLeft:6 }}>
                    {styleProfile?.emailsSent > 0 ? "active" : "auto"}
                  </span>
                </div>
                <span style={{ width:8, height:8, borderRadius:999, background:"#8b5cf6",
                  boxShadow:"0 0 6px #8b5cf6", flexShrink:0 }}/>
              </div>
              {/* Indian Languages */}
              <div style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}
                onClick={() => setInput("मुझे एक email लिखना है ")}>
                <span style={{ fontSize:15 }}>🌐</span>
                <div style={{ flex:1 }}>
                  <span style={{ fontSize:11, fontWeight:700, color:"#22d3ee" }}>Indian Languages</span>
                  <span style={{ fontSize:10, color:"#374151", marginLeft:6 }}>try it</span>
                </div>
                <span style={{ width:8, height:8, borderRadius:999, background:"#06b6d4",
                  boxShadow:"0 0 6px #06b6d4", flexShrink:0 }}/>
              </div>
            </div>
          </div>

          {/* ── CHAT HISTORY — below mailbox tabs ── */}
          {chatSessions.length > 0 && (
            <div style={{ padding:"10px 4px 4px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8, paddingLeft:2 }}>
                <div style={{ width:3, height:14, borderRadius:999, background:"linear-gradient(#8b5cf6,#3b82f6)" }}/>
                <span style={{ fontSize:10, fontWeight:900, color:"#6b7280", letterSpacing:".1em", textTransform:"uppercase" }}>Recent Chats</span>
                <span style={{ fontSize:10, color:"#374151", marginLeft:"auto" }}>{chatSessions.length}</span>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:4,
                maxHeight:180, overflowY:"auto",
                scrollbarWidth:"thin", scrollbarColor:"rgba(255,255,255,0.06) transparent" }}>
                {chatSessions.map((session, idx) => (
                  <motion.div key={session.id}
                    initial={{ opacity:0, x:-8 }} animate={{ opacity:1, x:0 }}
                    transition={{ delay: idx * 0.03 }}
                    onClick={() => loadChatSession(session)}
                    style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 10px",
                      borderRadius:10, cursor:"pointer",
                      background:"rgba(255,255,255,0.02)",
                      border:"1px solid rgba(255,255,255,0.04)",
                      transition:"all 0.15s" }}
                    onMouseEnter={e => { e.currentTarget.style.background="rgba(139,92,246,0.1)"; e.currentTarget.style.borderColor="rgba(139,92,246,0.2)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background="rgba(255,255,255,0.02)"; e.currentTarget.style.borderColor="rgba(255,255,255,0.04)"; }}>
                    <div style={{ width:6, height:6, borderRadius:999, flexShrink:0,
                      background:["#f97316","#ec4899","#8b5cf6","#3b82f6","#22c55e"][idx%5] }}/>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12, fontWeight:600, color:"#d1d5db",
                        overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {session.title}
                      </div>
                      <div style={{ fontSize:10, color:"#374151", marginTop:1 }}>
                        {new Date(session.savedAt).toLocaleDateString("en-IN",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}
                      </div>
                    </div>
                    <button onClick={e => deleteChatSession(session.id, e)}
                      style={{ opacity:0, width:18, height:18, borderRadius:6, border:"none",
                        background:"rgba(255,255,255,0.06)", color:"#6b7280",
                        cursor:"pointer", fontSize:10, flexShrink:0,
                        display:"flex", alignItems:"center", justifyContent:"center" }}
                      onMouseEnter={e => e.currentTarget.style.opacity=1}
                      onMouseLeave={e => e.currentTarget.style.opacity=0}>✕</button>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Quick try section */}
          <div style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 4px 6px" }}>
            <div style={{ flex:1, height:1, background:"rgba(255,255,255,0.06)" }}/>
            <span style={{ fontSize:9, fontWeight:800, color:"#374151", letterSpacing:".12em", textTransform:"uppercase" }}>Quick Start</span>
            <div style={{ flex:1, height:1, background:"rgba(255,255,255,0.06)" }}/>
          </div>

          {[
            { text:"Send a follow-up email to my client", icon:"📧" },
            { text:"Write a professional email to my boss", icon:"💼" },
            { text:"मुझे एक email भेजना है", icon:"🌐" },
            { text:"Apologize for the project delay", icon:"⚠️" },
          ].map((s, i) => (
            <button key={i} onClick={() => setInput(s.text)}
              style={{ width:"100%", textAlign:"left", padding:"7px 10px", borderRadius:10, display:"flex", alignItems:"center", gap:8,
                background:"none", border:"none", color:"#4b5563", fontSize:11, cursor:"pointer", fontFamily:"inherit" }}
              onMouseEnter={e => { e.currentTarget.style.background="rgba(255,255,255,0.04)"; e.currentTarget.style.color="#9ca3af"; }}
              onMouseLeave={e => { e.currentTarget.style.background="none"; e.currentTarget.style.color="#4b5563"; }}>
              <span style={{ fontSize:12 }}>{s.icon}</span>{s.text}
            </button>
          ))}
        </nav>

        {/* Bottom: dashboard link */}
        <div className="px-3 py-3" style={{ borderTop:"1px solid rgba(255,255,255,0.06)" }}>
          <a href="/dashboard"
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold transition hover:bg-white/5"
            style={{ color:"#4b5563" }}>
            📊 Analytics Dashboard
          </a>
        </div>
      </div>
      )}

      {/* ══════════════════════════════════════════════════════════
           MOBILE SIDEBAR OVERLAY — clean compact slide-in
      ═══════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            className="fixed inset-0 z-[200] flex"
            initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            style={{ background:"rgba(0,0,0,0.75)" }}
            onClick={() => setSidebarOpen(false)}>

            <motion.div
              initial={{ x:-300 }} animate={{ x:0 }} exit={{ x:-300 }}
              transition={{ type:"spring", stiffness:340, damping:32 }}
              onClick={e => e.stopPropagation()}
              style={{ width:"290px", background:"#0a0418", borderRight:"1px solid rgba(249,115,22,0.12)",
                       display:"flex", flexDirection:"column", height:"100%", overflowY:"auto" }}>

              {/* Header */}
              <div style={{ display:"flex", alignItems:"center", gap:"10px", padding:"14px 14px",
                            borderBottom:"1px solid rgba(255,255,255,0.06)", flexShrink:0 }}>
                <SantharionLogo size={28}/>
                <div style={{ flex:1 }}>
                  <div className="grad-text-ai" style={{ fontSize:"13px", fontWeight:900, lineHeight:1 }}>Santharion AI</div>
                  <div style={{ fontSize:"10px", color:"#22c55e", display:"flex", alignItems:"center", gap:4, marginTop:2 }}>
                    <span style={{ width:5, height:5, borderRadius:"50%", background:"#22c55e", display:"inline-block" }}/>
                    Online · Free
                  </div>
                </div>
                <button onClick={() => setSidebarOpen(false)}
                  style={{ background:"rgba(255,255,255,0.06)", border:"none", borderRadius:8,
                           width:28, height:28, color:"#9ca3af", fontSize:14, cursor:"pointer",
                           display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
              </div>

              {/* User block */}
              <div style={{ padding:"10px 12px", borderBottom:"1px solid rgba(255,255,255,0.06)", flexShrink:0 }}>
                {user ? (
                  <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 10px", borderRadius:10,
                                background:"rgba(34,197,94,0.06)", border:"1px solid rgba(34,197,94,0.15)" }}>
                    {user.picture
                      ? <img src={user.picture} alt="" style={{ width:28, height:28, borderRadius:"50%", objectFit:"cover", flexShrink:0 }}/>
                      : <div style={{ flexShrink:0 }}><AiAvatar name={user.name} size={28}/></div>}
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:"11px", fontWeight:700, color:"#e5e7eb", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{user.name}</div>
                      <div style={{ fontSize:"9px", color:"#4b5563", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{user.email}</div>
                    </div>
                    <button onClick={() => { handleSignOut(); setSidebarOpen(false); }}
                      style={{ background:"none", border:"none", color:"#6b7280", cursor:"pointer", fontSize:10, padding:"2px 6px",
                               borderRadius:6, flexShrink:0 }}>Sign out</button>
                  </div>
                ) : (
                  <motion.button onClick={() => { handleSignIn(); setSidebarOpen(false); }}
                    whileTap={{ scale:0.97 }}
                    style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                             padding:"9px 12px", borderRadius:10, fontWeight:700, fontSize:12, color:"#fff", border:"none",
                             background:"linear-gradient(135deg,#f97316,#ec4899)", cursor:"pointer" }}>
                    <GoogleIcon/> Sign in with Google
                  </motion.button>
                )}
              </div>

              {/* Nav */}
              <nav style={{ flex:1, padding:"10px 12px", overflowY:"auto" }}>

                {/* New Chat */}
                <motion.button whileTap={{ scale:0.97 }}
                  onClick={() => { clearChat(); setSidebarOpen(false); }}
                  style={{ width:"100%", display:"flex", alignItems:"center", gap:10, padding:"12px 14px", borderRadius:12,
                    background:"rgba(249,115,22,0.1)", border:"1px solid rgba(249,115,22,0.22)",
                    color:"#fb923c", fontWeight:800, fontSize:13, cursor:"pointer", marginBottom:12, textAlign:"left",
                    fontFamily:"inherit" }}>
                  ✏️ New Chat
                </motion.button>

                {/* ─── BIG ACTION TABS ─── */}
                <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:10 }}>
                  <div style={{ width:3, height:14, borderRadius:999, background:"linear-gradient(#f97316,#ec4899)", flexShrink:0 }}/>
                  <span style={{ fontSize:9, fontWeight:900, color:"#6b7280", letterSpacing:".12em", textTransform:"uppercase" }}>Your Mailbox</span>
                </div>

                {/* SCHEDULED */}
                {(() => {
                  const pending = scheduledEmails.filter(e=>!e.sent&&!e.failed).length;
                  return (
                    <motion.button whileTap={{ scale:0.97 }}
                      onClick={() => { setShowScheduled(true); setSidebarOpen(false); }}
                      style={{ width:"100%", marginBottom:4, padding:"13px 14px", borderRadius:14,
                        background: pending > 0 ? "linear-gradient(135deg,rgba(124,58,237,0.22),rgba(168,85,247,0.14))" : "linear-gradient(135deg,rgba(124,58,237,0.1),rgba(168,85,247,0.06))",
                        border: pending > 0 ? "2px solid rgba(124,58,237,0.5)" : "1.5px solid rgba(124,58,237,0.2)",
                        cursor:"pointer", display:"flex", alignItems:"center", gap:12, fontFamily:"inherit", textAlign:"left",
                        boxShadow: pending > 0 ? "0 0 20px rgba(124,58,237,0.2)" : "none" }}>
                      <div style={{ width:42, height:42, borderRadius:12, flexShrink:0,
                        background:"linear-gradient(135deg,#7c3aed,#a855f7)", fontSize:20,
                        boxShadow: pending>0 ? "0 4px 16px rgba(124,58,237,0.5)" : "0 4px 12px rgba(124,58,237,0.3)",
                        display:"flex", alignItems:"center", justifyContent:"center" }}>📅</div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:15, fontWeight:900, color:"#fff" }}>Scheduled</div>
                        <div style={{ fontSize:11, color: pending > 0 ? "#c4b5fd" : "#6b7280", marginTop:1 }}>
                          {pending > 0 ? `${pending} queued to send` : "No queued emails"}
                        </div>
                      </div>
                      {pending > 0
                        ? <span style={{ background:"rgba(124,58,237,0.3)", color:"#c4b5fd", borderRadius:999, padding:"4px 10px", fontSize:13, fontWeight:900, border:"1px solid rgba(124,58,237,0.4)" }}>{pending}</span>
                        : <span style={{ fontSize:16, opacity:0.4 }}>›</span>}
                    </motion.button>
                  );
                })()}

                {/* ─── CHAT HISTORY ─── */}
                {chatSessions.length > 0 && (
                  <div style={{ margin:"12px 0 4px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
                      <div style={{ width:3, height:14, borderRadius:999, background:"linear-gradient(#8b5cf6,#3b82f6)", flexShrink:0 }}/>
                      <span style={{ fontSize:10, fontWeight:900, color:"#6b7280", letterSpacing:".1em", textTransform:"uppercase" }}>Recent Chats</span>
                      <span style={{ fontSize:10, color:"#374151", marginLeft:"auto" }}>{chatSessions.length}</span>
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:4,
                      maxHeight:160, overflowY:"auto",
                      scrollbarWidth:"thin", scrollbarColor:"rgba(255,255,255,0.06) transparent" }}>
                      {chatSessions.map((session, idx) => (
                        <div key={session.id}
                          onClick={() => { loadChatSession(session); setSidebarOpen(false); }}
                          style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 12px",
                            borderRadius:10, cursor:"pointer",
                            background:"rgba(255,255,255,0.02)",
                            border:"1px solid rgba(255,255,255,0.05)" }}>
                          <div style={{ width:7, height:7, borderRadius:999, flexShrink:0,
                            background:["#f97316","#ec4899","#8b5cf6","#3b82f6","#22c55e"][idx%5] }}/>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:13, fontWeight:600, color:"#d1d5db",
                              overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                              {session.title}
                            </div>
                            <div style={{ fontSize:10, color:"#4b5563", marginTop:2 }}>
                              {new Date(session.savedAt).toLocaleDateString("en-IN",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}
                            </div>
                          </div>
                          <button onClick={e => deleteChatSession(session.id, e)}
                            style={{ width:22, height:22, borderRadius:6, border:"1px solid rgba(255,255,255,0.08)",
                              background:"rgba(255,255,255,0.04)", color:"#6b7280",
                              cursor:"pointer", fontSize:11, flexShrink:0,
                              display:"flex", alignItems:"center", justifyContent:"center",
                              fontFamily:"inherit" }}>✕</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ─── AUTO FEATURES strip ─── */}
                <div style={{ margin:"12px 0 8px", padding:"10px 12px", borderRadius:12,
                  background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)" }}>
                  <div style={{ fontSize:9, fontWeight:800, color:"#4b5563", letterSpacing:".12em", textTransform:"uppercase", marginBottom:8 }}>⚡ Always On</div>
                  {[
                    { icon:"⏰", label:"Follow-up Reminders", color:"#fbbf24", dot:"#f59e0b", count: followUps.length },
                    { icon:"🧠", label:"Style Learning", color:"#a78bfa", dot:"#8b5cf6" },
                    { icon:"🌐", label:"Indian Languages", color:"#22d3ee", dot:"#06b6d4",
                      action: () => { setInput("मुझे एक email लिखना है "); setSidebarOpen(false); } },
                  ].map((f, i) => (
                    <div key={i} onClick={f.action}
                      style={{ display:"flex", alignItems:"center", gap:10, paddingBottom: i<2 ? 7 : 0,
                        marginBottom: i<2 ? 7 : 0,
                        borderBottom: i<2 ? "1px solid rgba(255,255,255,0.05)" : "none",
                        cursor: f.action ? "pointer" : "default" }}>
                      <span style={{ fontSize:15 }}>{f.icon}</span>
                      <span style={{ flex:1, fontSize:12, fontWeight:700, color:f.color }}>{f.label}</span>
                      {f.count > 0 && <span style={{ background:f.dot, color:"#fff", borderRadius:999, padding:"2px 7px", fontSize:10, fontWeight:800 }}>{f.count}</span>}
                      <span style={{ width:8, height:8, borderRadius:999, background:f.dot, boxShadow:`0 0 6px ${f.dot}`, flexShrink:0 }}/>
                    </div>
                  ))}
                </div>

                {/* ─── Quick tries ─── */}
                <div style={{ fontSize:9, fontWeight:800, color:"#4b5563", padding:"4px 2px 8px", letterSpacing:".12em", textTransform:"uppercase" }}>Try These</div>
                {["Send a follow-up email to my client","Write a professional email to my boss",
                  "मुझे एक email भेजना है","Apologize for the project delay"].map((s, i) => (
                  <button key={i} onClick={() => { setInput(s); setSidebarOpen(false); }}
                    style={{ width:"100%", textAlign:"left", padding:"8px 10px", borderRadius:10,
                      background:"none", border:"none", color:"#4b5563", fontSize:12,
                      cursor:"pointer", marginBottom:2, fontFamily:"inherit" }}
                    onMouseEnter={e => { e.currentTarget.style.background="rgba(255,255,255,0.04)"; e.currentTarget.style.color="#9ca3af"; }}
                    onMouseLeave={e => { e.currentTarget.style.background="none"; e.currentTarget.style.color="#4b5563"; }}>
                    {s}
                  </button>
                ))}
              </nav>

              {/* Footer */}
              <div style={{ padding:"10px 12px", borderTop:"1px solid rgba(255,255,255,0.06)", flexShrink:0 }}>
                <a href="/dashboard"
                  style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 10px", borderRadius:8,
                           color:"#4b5563", fontSize:10, fontWeight:600, textDecoration:"none" }}>
                  📊 Analytics Dashboard
                </a>
              </div>

            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══════════════════════════════════════════════════════════
           RIGHT SIDE: CHAT AREA
      ═══════════════════════════════════════════════════════════ */}
      <div className="flex-1 min-w-0 relative"
        style={{ background:"linear-gradient(160deg,#080410 0%,#0f0720 50%,#080410 100%)", overflow:"hidden", display:"grid", gridTemplateRows:"auto 1fr auto" }}>

        {/* Top bar — desktop shows title, mobile shows hamburger */}
        {!isMobile ? (
        <div style={{ flexShrink:0, display:"flex", alignItems:"center", justifyContent:"space-between",
                      padding:"10px 20px", borderBottom:"1px solid rgba(255,255,255,0.06)",
                      background:"rgba(8,4,16,0.75)", backdropFilter:"blur(20px)" }}>
          {/* Left: title + status */}
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div>
              <p style={{ fontSize:14, fontWeight:800, color:"#f1f5f9", margin:0, letterSpacing:"-0.3px" }}>✉️ AI Email Assistant</p>
              <p style={{ fontSize:10, color:"#374151", margin:0, marginTop:1 }}>
                {user ? `Signed in as ${user.email}` : "Sign in with Google to send emails"}
              </p>
            </div>
            {trackedThreads.length > 0 && (
              <div style={{ display:"flex", alignItems:"center", gap:5, padding:"4px 10px",
                            borderRadius:999, background:"rgba(34,197,94,0.08)", border:"1px solid rgba(34,197,94,0.2)" }}>
                <span style={{ width:6, height:6, borderRadius:"50%", background:"#22c55e", display:"inline-block",
                               animation:"pulse 1.5s ease infinite" }}/>
                <span style={{ fontSize:10, color:"#4ade80", fontWeight:600 }}>Watching {trackedThreads.length} thread{trackedThreads.length>1?"s":""}</span>
              </div>
            )}
          </div>
          {/* Right: action buttons */}
          <div style={{ display:"flex", gap:6, alignItems:"center" }}>

            <button onClick={() => setShowSent(true)}
              style={{ display:"flex", alignItems:"center", gap:6, padding:"5px 11px",
                       background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.09)",
                       borderRadius:10, color:"#9ca3af", cursor:"pointer", fontSize:11, fontWeight:600, fontFamily:"inherit" }}>
              📤 Sent {sentEmails.length > 0 && <span style={{ background:"rgba(34,197,94,0.15)", color:"#4ade80", borderRadius:999, padding:"1px 5px", fontSize:9, fontWeight:900 }}>{sentEmails.length}</span>}
            </button>
            <button onClick={() => setShowOnboarding(true)}
              style={{ padding:"5px 10px", background:"rgba(139,92,246,0.08)", border:"1px solid rgba(139,92,246,0.18)",
                       borderRadius:10, color:"#a78bfa", cursor:"pointer", fontSize:11, fontWeight:700, fontFamily:"inherit" }}>
              ? How it works
            </button>
            <button onClick={() => setShowInsights(v => !v)}
              style={{ padding:"5px 10px", background: showInsights ? "rgba(249,115,22,0.1)" : "rgba(255,255,255,0.04)",
                       border: showInsights ? "1px solid rgba(249,115,22,0.3)" : "1px solid rgba(255,255,255,0.08)",
                       borderRadius:10, color: showInsights ? "#fb923c" : "#6b7280",
                       cursor:"pointer", fontSize:11, fontWeight:700, fontFamily:"inherit" }}>
              {showInsights ? "✨ Hide Panel" : "✨ AI Panel"}
            </button>
            <button onClick={clearChat}
              style={{ padding:"6px 8px", background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)",
                       borderRadius:10, color:"#6b7280", cursor:"pointer" }}>
              <FaTrash size={12}/>
            </button>
          </div>
        </div>
        ) : (
        <div style={{ flexShrink:0, display:"flex", alignItems:"center", justifyContent:"space-between",
                      padding:"10px 14px", borderBottom:"1px solid rgba(255,255,255,0.06)",
                      background:"rgba(8,4,16,0.97)", backdropFilter:"blur(20px)", zIndex:10 }}>

          {/* Hamburger */}
          <button onClick={() => setSidebarOpen(true)}
            style={{ background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.12)",
                     borderRadius:8, width:34, height:34, display:"flex", alignItems:"center",
                     justifyContent:"center", cursor:"pointer", color:"#e5e7eb", fontSize:16, flexShrink:0 }}>
            ☰
          </button>

          {/* Logo center */}
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <SantharionLogo size={22}/>
            <span className="grad-text-ai" style={{ fontSize:13, fontWeight:900 }}>Santharion AI</span>
          </div>

          {/* Right actions */}
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>

            {!user ? (
              <motion.button onClick={handleSignIn} whileTap={{ scale:0.95 }}
                style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 10px", borderRadius:8,
                         fontSize:11, fontWeight:700, color:"#fff", border:"none",
                         background:"linear-gradient(135deg,#f97316,#ec4899)", cursor:"pointer", whiteSpace:"nowrap" }}>
                <GoogleIcon/> Sign in
              </motion.button>
            ) : (
              <button onClick={clearChat}
                style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)",
                         borderRadius:8, width:32, height:32, display:"flex", alignItems:"center",
                         justifyContent:"center", cursor:"pointer", color:"#6b7280" }}>
                <FaTrash size={11}/>
              </button>
            )}
          </div>
        </div>
        )}

        {/* ── CHAT + INPUT WRAPPER ── */}
        <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden", position:"relative", minHeight:0 }}>

        {/* Video background behind chat */}
        <div className="absolute inset-0 pointer-events-none z-0" style={{ overflow:"hidden" }}>
          {/* Dark base */}
          <div style={{ position:"absolute", inset:0, background:"#07030f" }}/>

          {/* Looping background video */}
          <video
            autoPlay muted loop playsInline
            style={{
              position:"absolute", top:"50%", left:"50%",
              transform:"translate(-50%,-50%)",
              minWidth:"100%", minHeight:"100%",
              width:"auto", height:"auto",
              objectFit:"cover",
              opacity: 0.28,
              filter:"saturate(1.8) hue-rotate(220deg) brightness(0.65) contrast(1.15)",
            }}>
            <source src="/bg-video.mp4" type="video/mp4"/>
          </video>

          {/* Top fade — keeps chat header readable */}
          <div style={{
            position:"absolute", top:0, left:0, right:0, height:"120px",
            background:"linear-gradient(to bottom, #07030f, transparent)",
          }}/>
          {/* Bottom fade */}
          <div style={{
            position:"absolute", bottom:0, left:0, right:0, height:"120px",
            background:"linear-gradient(to top, #07030f, transparent)",
          }}/>
          {/* Dark overlay to keep text readable */}
          <div style={{
            position:"absolute", inset:0,
            background:"rgba(7,3,15,0.55)",
          }}/>
          {/* Brand color wash — purple left, orange right */}
          <div style={{
            position:"absolute", inset:0,
            background:"radial-gradient(ellipse 60% 80% at 0% 50%, rgba(139,92,246,0.08) 0%, transparent 60%), radial-gradient(ellipse 50% 60% at 100% 50%, rgba(249,115,22,0.06) 0%, transparent 60%)",
          }}/>
        </div>
        <div ref={chatAreaRef} className="chat-area overflow-y-auto px-5 py-5 space-y-5"
          style={{ overflowAnchor:"none", position:"relative", zIndex:1, minHeight:0, paddingBottom:"20px", flex:1 }}>

          {/* Quick suggestions — only on first load */}
          {messages.length === 1 && (
            <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.1 }}
              style={{ marginBottom: isMobile ? 8 : 16 }}>
              {/* Welcome card — desktop only */}
              {!isMobile && (
                <div style={{ textAlign:"center", padding:"24px 20px 20px", borderRadius:24,
                  background:"linear-gradient(135deg,rgba(139,92,246,0.06),rgba(249,115,22,0.04))",
                  border:"1px solid rgba(255,255,255,0.07)", marginBottom:16 }}>
                  <motion.div animate={{ rotate:[0,5,-5,0] }} transition={{ repeat:Infinity, duration:8, ease:"easeInOut" }}
                    style={{ display:"inline-block", marginBottom:14 }}>
                    <SantharionLogo size={52}/>
                  </motion.div>
                  <h2 style={{ fontSize:20, fontWeight:800, marginBottom:8,
                    background:"linear-gradient(135deg,#f97316,#ec4899,#8b5cf6)", WebkitBackgroundClip:"text",
                    WebkitTextFillColor:"transparent", backgroundClip:"text", lineHeight:1.2 }}>
                    {user ? `Hey ${user.name?.split(" ")[0]}! 👋 Ready to send?` : "Hey! I'm Santharion AI 👋"}
                  </h2>
                  <p style={{ fontSize:12, color:"#64748b", lineHeight:1.7, maxWidth:380, margin:"0 auto 16px" }}>
                    {user
                      ? "Just tell me who to email and what to say. I'll write 3 perfectly toned versions — Professional, Friendly, or Brief — and send from your Gmail in one click."
                      : "I write & send professional emails from your Gmail in seconds. Just describe what you want to say — I handle everything else. 100% free."}
                  </p>
                  {/* How it works — 3 steps */}
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom: user ? 0 : 14 }}>
                    {[
                      { step:"1", icon:"💬", title:"Describe it", desc:"Tell me who to email & what to say" },
                      { step:"2", icon:"⚡", title:"Pick a template", desc:"Choose from 3 AI-written versions" },
                      { step:"3", icon:"📤", title:"Send!", desc:"One click sends from your Gmail" },
                    ].map((s,i) => (
                      <div key={i} style={{ padding:"10px 8px", borderRadius:14,
                        background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", textAlign:"center" }}>
                        <div style={{ fontSize:18, marginBottom:4 }}>{s.icon}</div>
                        <div style={{ fontSize:11, fontWeight:700, color:"#e2e8f0", marginBottom:3 }}>{s.title}</div>
                        <div style={{ fontSize:10, color:"#475569", lineHeight:1.4 }}>{s.desc}</div>
                      </div>
                    ))}
                  </div>
                  {!user && (
                    <motion.button onClick={handleSignIn} whileHover={{ scale:1.03 }} whileTap={{ scale:0.97 }}
                      style={{ marginTop:0, display:"inline-flex", alignItems:"center", gap:8, padding:"10px 22px",
                        borderRadius:12, border:"none", cursor:"pointer", fontFamily:"inherit",
                        background:"linear-gradient(135deg,#f97316,#ec4899)", color:"#fff", fontSize:13, fontWeight:700,
                        boxShadow:"0 4px 20px rgba(249,115,22,0.35)" }}>
                      <GoogleIcon/> Sign in with Google — it's free
                    </motion.button>
                  )}
                </div>
              )}
              {isMobile ? (
                <div style={{ display:"flex", gap:8, overflowX:"auto", paddingBottom:4, scrollbarWidth:"none" }}>
                  {[
                    { text:"Follow-up email",    full:"Send a follow-up email to my client",   icon:"📧", color:"#f97316" },
                    { text:"Email to boss",       full:"Write a professional email to my boss", icon:"💼", color:"#ec4899" },
                    { text:"Thank you email",     full:"Email a thank you after the interview", icon:"🤝", color:"#8b5cf6" },
                    { text:"Apologize for delay", full:"Apologize for the project delay",       icon:"⚠️", color:"#f59e0b" },
                  ].map((s, i) => (
                    <motion.button key={i} onClick={() => sendMessage(s.full)} whileTap={{ scale:0.95 }}
                      style={{ flexShrink:0, display:"flex", alignItems:"center", gap:5,
                               padding:"7px 12px", borderRadius:20, border:`1px solid ${s.color}30`,
                               background:`${s.color}10`, color:"#9ca3af", fontSize:11,
                               fontWeight:500, cursor:"pointer", whiteSpace:"nowrap" }}>
                      <span style={{ fontSize:13 }}>{s.icon}</span>{s.text}
                    </motion.button>
                  ))}
                </div>
              ) : (
                <div style={{ marginBottom:8 }}>
                  <p style={{ fontSize:10, fontWeight:700, color:"#374151", marginBottom:8, textAlign:"center", letterSpacing:".08em", textTransform:"uppercase" }}>
                    👇 Click any to get started instantly
                  </p>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                    {[
                      { text:"Send a follow-up email to my client",   icon:"📧", color:"#f97316", label:"Follow-up" },
                      { text:"Write a professional email to my boss", icon:"💼", color:"#ec4899", label:"To Boss" },
                      { text:"Email a thank you after the interview", icon:"🤝", color:"#8b5cf6", label:"Thank You" },
                      { text:"Apologize for the project delay",       icon:"⚠️", color:"#f59e0b", label:"Apology" },
                    ].map((s, i) => (
                      <motion.button key={i} onClick={() => sendMessage(s.text)}
                        initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }}
                        transition={{ delay: 0.1 + i*0.07 }}
                        whileHover={{ scale:1.02, y:-2, borderColor:`${s.color}60` }}
                        whileTap={{ scale:0.97 }}
                        style={{ display:"flex", alignItems:"flex-start", gap:10, padding:"12px 14px",
                          borderRadius:16, textAlign:"left", cursor:"pointer", fontFamily:"inherit",
                          background:`${s.color}08`, border:`1px solid ${s.color}18`, color:"#9ca3af",
                          transition:"all 0.2s" }}>
                        <div style={{ width:32, height:32, borderRadius:10, background:`${s.color}15`,
                          display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0 }}>
                          {s.icon}
                        </div>
                        <div>
                          <div style={{ fontSize:11, fontWeight:700, color:"#e2e8f0", marginBottom:2 }}>{s.label}</div>
                          <div style={{ fontSize:11, color:"#64748b", lineHeight:1.4 }}>{s.text}</div>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          <AnimatePresence initial={false}>
            {messages.map((msg, i) => renderMsg(msg, i))}
          </AnimatePresence>

          {/* Thinking */}
          {loading && (
            <motion.div initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }}
              className="flex items-end gap-3">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background:"rgba(249,115,22,0.1)", border:"1px solid rgba(249,115,22,0.25)" }}>
                <SantharionLogo size={20}/>
              </div>
              <div className="px-5 py-3.5 rounded-2xl rounded-bl-sm flex items-center gap-3"
                style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(249,115,22,0.2)" }}>
                <span className="text-xs font-medium" style={{ color:"#9ca3af" }}>Thinking</span>
                <div className="flex gap-1.5">
                  {[0,150,300].map((d,i) => (
                    <motion.span key={i}
                      animate={{ y:[0,-6,0], scale:[1,1.3,1] }}
                      transition={{ repeat:Infinity, duration:0.7, delay:d/1000 }}
                      className="w-2 h-2 rounded-full inline-block"
                      style={{ background:["#f97316","#ec4899","#8b5cf6"][i] }}/>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
          <div ref={bottomRef}/>
        </div>

        {/* ── INPUT BAR — hidden when compose modal is open ── */}
        <div style={{
            flexShrink:0, padding:"8px 16px 12px", zIndex:10,
            position:"relative",
            background:"linear-gradient(to top, rgba(7,3,15,1) 70%, transparent)",
            display: composeModalOpen ? "none" : "block",
          }}>
          <div
            className="rounded-2xl transition-all duration-300"
            style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.1)" }}>

            {/* Voice indicator */}
            <AnimatePresence>
              {voice.listening && (
                <motion.div initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:"auto" }} exit={{ opacity:0, height:0 }}
                  className="px-4 py-2 flex items-center gap-2 rounded-t-2xl"
                  style={{ background:"rgba(249,115,22,0.08)", borderBottom:"1px solid rgba(249,115,22,0.15)" }}>
                  <motion.div animate={{ scale:[1,1.4,1] }} transition={{ repeat:Infinity, duration:0.8 }}
                    className="w-2 h-2 rounded-full" style={{ background:"#f97316" }}/>
                  <span className="text-xs font-semibold" style={{ color:"#fb923c" }}>🎤 Listening…</span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Tone indicator inside input */}
            <div style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 14px 0", flexWrap:"wrap" }}>
              <span style={{ fontSize:9, color:"#374151", fontWeight:600 }}>TONE:</span>
              {["Professional","Friendly","Brief","Formal"].map(tone => (
                <button key={tone} onClick={() => setActiveTone(tone)}
                  style={{ padding:"3px 8px", borderRadius:999, fontSize:9, fontWeight:700, cursor:"pointer", fontFamily:"inherit",
                    background: activeTone===tone ? "linear-gradient(135deg,rgba(249,115,22,0.25),rgba(236,72,153,0.18))" : "rgba(255,255,255,0.04)",
                    border: activeTone===tone ? "1px solid rgba(249,115,22,0.45)" : "1px solid rgba(255,255,255,0.08)",
                    color: activeTone===tone ? "#fb923c" : "#4b5563",
                    transition:"all 0.15s" }}>
                  {tone==="Professional"?"🏢":tone==="Friendly"?"😊":tone==="Brief"?"⚡":"🤝"} {tone}
                </button>
              ))}
            </div>
            <div className="flex items-end gap-2 p-2.5">
              <textarea rows={1} value={input}
                onChange={e => { setInput(e.target.value); e.target.style.height="auto"; e.target.style.height=Math.min(e.target.scrollHeight,120)+"px"; }}
                onKeyDown={e => {
                  // Desktop only: Enter sends, Shift+Enter = new line
                  // Mobile: never intercept Enter (let keyboard handle it naturally)
                  if(!isMobile && e.key==="Enter" && !e.shiftKey){ e.preventDefault(); sendMessage(); }
                }}
                placeholder={voice.listening ? "🎤 Listening…" : "✉️  Describe your email… e.g. 'Send a follow-up to my client'"}
                className="flex-1 bg-transparent px-3 py-2.5 outline-none resize-none text-sm leading-relaxed placeholder-gray-600"
                style={{ color:"#f3f4f6", minHeight:"40px", maxHeight:"120px", overflowY:"auto" }}/>
              <motion.button onClick={voice.toggle}
                animate={voice.listening ? { scale:[1,1.15,1] } : {}}
                transition={{ repeat:Infinity, duration:0.8 }}
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition"
                style={{
                  background: voice.listening ? "rgba(249,115,22,0.2)" : "rgba(255,255,255,0.06)",
                  border: voice.listening ? "1px solid rgba(249,115,22,0.5)" : "1px solid rgba(255,255,255,0.1)",
                  color: voice.listening ? "#f97316" : "#6b7280",
                }}>
                {voice.listening ? <FaMicrophoneSlash size={14}/> : <FaMicrophone size={14}/>}
              </motion.button>
              <motion.button onClick={() => sendMessage()} disabled={loading || !input.trim()}
                whileHover={{ scale: loading||!input.trim() ? 1 : 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-10 h-10 rounded-xl flex items-center justify-center text-white flex-shrink-0 transition disabled:opacity-30"
                style={{ background:"linear-gradient(135deg,#f97316,#ec4899)", boxShadow:"0 4px 14px rgba(249,115,22,0.3)" }}>
                <FaPaperPlane size={13}/>
              </motion.button>
            </div>

            <div className="px-4 pb-2.5 flex items-center justify-between flex-wrap gap-1">
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span className="text-xs" style={{ color:"#374151" }}>{isMobile ? "Tap ✈️ to send" : "Enter to send · Shift+Enter for new line"}</span>
                {activeTone && (
                  <span style={{ fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:999, background:"rgba(249,115,22,0.1)", border:"1px solid rgba(249,115,22,0.2)", color:"#fb923c" }}>
                    {activeTone==="Professional"?"🏢":activeTone==="Friendly"?"😊":activeTone==="Brief"?"⚡":"🤝"} {activeTone}
                  </span>
                )}
              </div>
              {user
                ? <span className="text-xs flex items-center gap-1" style={{ color:"#4ade80" }}><FaShieldAlt size={9}/> {user.email}</span>
                : <span className="text-xs" style={{ color:"#374151" }}>Powered by Santharion AI</span>}
            </div>
          </div>
        </div>

        </div>{/* ── END CHAT+INPUT WRAPPER ── */}
      </div>

      {/* ── RIGHT INSIGHTS PANEL ── */}
      {showInsights && !isMobile && (
        <div className="insights-panel">
          <div style={{ padding:"14px 16px 12px", borderBottom:"1px solid rgba(255,255,255,0.07)", flexShrink:0 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div>
                <div style={{ fontSize:13, fontWeight:800, color:"#e5e7eb" }}>✨ AI Panel</div>
                <div style={{ fontSize:10, color:"#374151", marginTop:2 }}>Tone · Scores · Threads · Memory</div>
              </div>
              <button onClick={() => setShowInsights(false)}
                style={{ width:24, height:24, borderRadius:8, background:"rgba(255,255,255,0.05)",
                  border:"1px solid rgba(255,255,255,0.08)", color:"#4b5563", cursor:"pointer", fontSize:12,
                  display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
            </div>
          </div>

          {/* Email Quality Score */}
          {emailScore && (
            <div style={{ padding:"14px 14px 0" }}>
              <div style={{ fontSize:9, fontWeight:800, color:"#374151", letterSpacing:".12em", textTransform:"uppercase", marginBottom:8 }}>📊 Email Quality Score</div>
              <div style={{ padding:"12px 14px", borderRadius:14, background:"linear-gradient(135deg,rgba(249,115,22,0.06),rgba(236,72,153,0.04))", border:"1px solid rgba(249,115,22,0.15)" }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                  <span style={{ fontSize:11, color:"#9ca3af" }}>Last email sent</span>
                  <span style={{ fontSize:22, fontWeight:800, background:"linear-gradient(135deg,#f97316,#ec4899)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>{emailScore.overall}</span>
                </div>
                {[["Clarity",emailScore.clarity,"#4ade80"],["Tone",emailScore.tone,"#f97316"],["Urgency",emailScore.urgency,"#ec4899"]].map(([l,v,c]) => (
                  <div key={l} style={{ marginBottom:7 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:"#4b5563", marginBottom:3 }}><span>{l}</span><span style={{ color:c }}>{v}%</span></div>
                    <div style={{ height:4, borderRadius:999, background:"rgba(255,255,255,0.05)", overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${v}%`, borderRadius:999, background:`linear-gradient(90deg,${c},${c}99)` }}/>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}



          {/* Active Threads with mood */}
          {trackedThreads.length > 0 && (
            <div style={{ padding:"14px 14px 0" }}>
              <div style={{ fontSize:9, fontWeight:800, color:"#374151", letterSpacing:".12em", textTransform:"uppercase", marginBottom:8 }}>📬 Active Threads</div>
              {trackedThreads.slice(0,4).map((t,i) => {
                return (
                  <div key={i} className="insight-card" style={{ display:"flex", gap:10, padding:"10px 11px", borderRadius:12, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", marginBottom:6 }}>
                    <div style={{ width:32, height:32, borderRadius:10, background:"linear-gradient(135deg,#6366f1,#8b5cf6)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:800, flexShrink:0 }}>
                      {(t.to||"?")[0].toUpperCase()}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:"#e2e8f0", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{t.to}</div>
                      <div style={{ fontSize:10, color:"#4b5563", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", marginTop:2 }}>{t.subject}</div>
                      <div style={{ marginTop:4 }}>
                        <span style={{ fontSize:9, color:"#374151" }}>👁 Watching for reply</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Email Memory */}
          {Object.keys(emailMemory).length > 0 && (
            <div style={{ padding:"14px 14px 0" }}>
              <div style={{ fontSize:9, fontWeight:800, color:"#374151", letterSpacing:".12em", textTransform:"uppercase", marginBottom:8 }}>🧠 Email Memory</div>
              {Object.values(emailMemory).sort((a,b) => b.count - a.count).slice(0,4).map((m,i) => (
                <button key={i} onClick={() => setInput(`Send an email to ${m.to} `)}
                  className="insight-card"
                  style={{ display:"flex", alignItems:"center", gap:9, padding:"8px 10px", borderRadius:11, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", marginBottom:5, cursor:"pointer", width:"100%", textAlign:"left", fontFamily:"inherit" }}>
                  <div style={{ width:28, height:28, borderRadius:9, background:"linear-gradient(135deg,rgba(139,92,246,0.3),rgba(236,72,153,0.2))", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800, color:"#a78bfa", flexShrink:0 }}>
                    {m.to[0].toUpperCase()}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:11, fontWeight:600, color:"#d1d5db", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{m.to}</div>
                    <div style={{ fontSize:9, color:"#374151", marginTop:1 }}>Emailed {m.count}x · Click to email again</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Tone Switcher always visible hint */}
          <div style={{ padding:"0 14px 14px" }}>
            <div style={{ fontSize:9, fontWeight:800, color:"#374151", letterSpacing:".12em", textTransform:"uppercase", marginBottom:8 }}>
              🎛 Active Tone
            </div>
            <div style={{ padding:"10px 12px", borderRadius:12, background:"rgba(249,115,22,0.06)", border:"1px solid rgba(249,115,22,0.15)" }}>
              <div style={{ fontSize:12, fontWeight:700, color:"#fb923c", marginBottom:3 }}>
                {activeTone === "Professional" ? "🏢" : activeTone === "Friendly" ? "😊" : activeTone === "Brief" ? "⚡" : "🤝"} {activeTone}
              </div>
              <div style={{ fontSize:10, color:"#64748b", lineHeight:1.5 }}>
                {activeTone === "Professional" ? "Formal & structured. Great for business emails." :
                 activeTone === "Friendly" ? "Warm & conversational. Great for colleagues." :
                 activeTone === "Brief" ? "Short & straight to the point. Saves time." :
                 "Very formal. Best for senior management or legal."}
              </div>
            </div>
          </div>

          {/* Feature showcase — shown when no data yet */}
          {!emailScore && trackedThreads.length === 0 && Object.keys(emailMemory).length === 0 && (
            <div style={{ padding:"0 14px 14px" }}>
              <div style={{ fontSize:9, fontWeight:800, color:"#374151", letterSpacing:".12em", textTransform:"uppercase", marginBottom:8 }}>
                🚀 What I'll show here
              </div>
              {[
                { icon:"📊", title:"Email Quality Score", desc:"Your emails scored on Clarity, Tone & Urgency (0-100)", color:"#f97316" },
                { icon:"📬", title:"Thread Tracker", desc:"See who replied, who's still waiting — at a glance", color:"#4ade80" },
                { icon:"🧠", title:"Email Memory", desc:"Quick-email people you contact often — 1 click", color:"#8b5cf6" },
                { icon:"🌡", title:"Reply Mood", desc:"AI detects if replies are positive, negative or urgent", color:"#ec4899" },
                { icon:"📅", title:"Smart Scheduler", desc:"Best time to send based on recipient's timezone", color:"#22d3ee" },
              ].map((f,i) => (
                <div key={i} style={{ display:"flex", gap:10, padding:"9px 11px", borderRadius:12,
                  background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)",
                  marginBottom:5 }}>
                  <div style={{ width:30, height:30, borderRadius:9, background:`${f.color}15`,
                    display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, flexShrink:0 }}>
                    {f.icon}
                  </div>
                  <div>
                    <div style={{ fontSize:11, fontWeight:700, color:"#e2e8f0", marginBottom:2 }}>{f.title}</div>
                    <div style={{ fontSize:10, color:"#475569", lineHeight:1.4 }}>{f.desc}</div>
                  </div>
                </div>
              ))}
              <div style={{ textAlign:"center", marginTop:10, padding:"10px", borderRadius:10,
                background:"rgba(249,115,22,0.05)", border:"1px solid rgba(249,115,22,0.12)" }}>
                <div style={{ fontSize:10, color:"#f97316", fontWeight:600 }}>✉️ Send your first email to unlock all insights!</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── REPLY ALERT TOAST ── */}
      <AnimatePresence>
        {replyAlert && (
          <motion.div
            initial={{ opacity:0, y: isMobile ? 60 : -60, scale:0.95 }}
            animate={{ opacity:1, y:0, scale:1 }}
            exit={{ opacity:0, y: isMobile ? 60 : -60, scale:0.95 }}
            transition={{ type:"spring", stiffness:400, damping:30 }}
            style={{
              position:"fixed",
              top: isMobile ? "auto" : 16,
              bottom: isMobile ? 16 : "auto",
              left:"50%", transform:"translateX(-50%)",
              zIndex:99999,
              width: isMobile ? "calc(100vw - 32px)" : "auto",
              minWidth: isMobile ? "auto" : 300,
              maxWidth: isMobile ? "auto" : 380,
            }}>
            {/* Scheduled send success — simple green pill */}
            {replyAlert.from?.includes("Scheduled") ? (
              <div style={{ display:"flex", alignItems:"center", gap:10,
                padding:"10px 16px", borderRadius:12,
                background:"linear-gradient(135deg,rgba(34,197,94,0.18),rgba(16,185,129,0.12))",
                border:"1px solid rgba(34,197,94,0.4)",
                boxShadow:"0 4px 20px rgba(0,0,0,0.5)" }}>
                <span style={{ fontSize:18 }}>✅</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:800, color:"#4ade80" }}>
                    {replyAlert.from}
                  </div>
                  <div style={{ fontSize:11, color:"#6b7280", overflow:"hidden",
                    textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {replyAlert.subject}
                  </div>
                </div>
                <button onClick={() => setReplyAlert(null)}
                  style={{ background:"none", border:"none", color:"#4b5563",
                    cursor:"pointer", fontSize:16, padding:0, lineHeight:1, flexShrink:0 }}>✕</button>
              </div>
            ) : (
              /* New reply — compact card with Reply button */
              <div style={{ padding:"12px 14px", borderRadius:14,
                background:"linear-gradient(135deg,#1a0a2e,#0f1a2e)",
                border:"1px solid rgba(249,115,22,0.4)",
                boxShadow:"0 6px 30px rgba(0,0,0,0.6)" }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                    <motion.span animate={{ scale:[1,1.3,1] }} transition={{ repeat:Infinity, duration:1.4 }}
                      style={{ width:8, height:8, borderRadius:"50%", background:"#f97316",
                        display:"inline-block", boxShadow:"0 0 6px #f97316" }}/>
                    <span style={{ fontSize:12, fontWeight:800, color:"#f97316" }}>New reply</span>
                    {replyAlert.mood && replyAlert.mood !== "neutral" && (
                      <span style={{ fontSize:10, fontWeight:700, padding:"1px 6px", borderRadius:999,
                        background: replyAlert.mood==="positive" ? "rgba(34,197,94,0.2)" : replyAlert.mood==="urgent" ? "rgba(245,158,11,0.2)" : "rgba(239,68,68,0.2)",
                        color: replyAlert.mood==="positive" ? "#4ade80" : replyAlert.mood==="urgent" ? "#fbbf24" : "#f87171" }}>
                        {replyAlert.mood==="positive" ? "😊" : replyAlert.mood==="urgent" ? "🚨" : "😠"}
                      </span>
                    )}
                  </div>
                  <button onClick={() => setReplyAlert(null)}
                    style={{ background:"none", border:"none", color:"#4b5563",
                      cursor:"pointer", fontSize:15, padding:0, lineHeight:1 }}>✕</button>
                </div>
                <div style={{ fontSize:12, color:"#9ca3af", marginBottom:2,
                  overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  <span style={{ color:"#d1d5db" }}>From: </span>{replyAlert.from}
                </div>
                <div style={{ fontSize:12, color:"#9ca3af", marginBottom:10,
                  overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  <span style={{ color:"#d1d5db" }}>Re: </span>{replyAlert.subject}
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={() => setReplyAlert(null)}
                    style={{ padding:"8px 12px", borderRadius:8, fontSize:12,
                      background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)",
                      color:"#6b7280", cursor:"pointer", fontFamily:"inherit" }}>
                    Dismiss
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── SENT PANEL ── */}
      <AnimatePresence>
        {showSent && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            style={{ position:"fixed", inset:0, zIndex:10000, background:"rgba(0,0,0,0.7)",
              backdropFilter:"blur(8px)", display:"flex",
              alignItems: isMobile ? "flex-end" : "flex-start",
              justifyContent:"flex-start" }}
            onClick={() => setShowSent(false)}>
            <motion.div
              initial={{ x: isMobile ? 0 : -460, y: isMobile ? 100 : 0 }}
              animate={{ x:0, y:0 }}
              exit={{ x: isMobile ? 0 : -460, y: isMobile ? 100 : 0 }}
              transition={{ type:"spring", stiffness:340, damping:34 }}
              onClick={e => e.stopPropagation()}
              style={{
                width: isMobile ? "100%" : 460,
                height: isMobile ? "85vh" : "100vh",
                borderRadius: isMobile ? "28px 28px 0 0" : 0,
                background:"linear-gradient(180deg,#071a10 0%,#040d08 100%)",
                borderRight: isMobile ? "none" : "2px solid rgba(34,197,94,0.15)",
                display:"flex", flexDirection:"column", overflow:"hidden",
                boxShadow: isMobile ? "0 -24px 80px rgba(0,0,0,0.7)" : "24px 0 80px rgba(0,0,0,0.6)"
              }}>

              {isMobile && <div style={{ width:40, height:4, borderRadius:999,
                background:"rgba(255,255,255,0.15)", margin:"12px auto 0", flexShrink:0 }}/>}
              {/* Header */}
              <div style={{ padding: isMobile ? "12px 16px 10px" : "16px 20px 14px", flexShrink:0,
                background:"linear-gradient(135deg,rgba(34,197,94,0.14),rgba(16,185,129,0.07))",
                borderBottom:"1px solid rgba(34,197,94,0.12)" }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                    <div style={{ width:42, height:42, borderRadius:13, flexShrink:0,
                      background:"linear-gradient(135deg,#22c55e,#16a34a)",
                      boxShadow:"0 6px 20px rgba(34,197,94,0.35)",
                      display:"flex", alignItems:"center", justifyContent:"center", fontSize:22 }}>📤</div>
                    <div>
                      <div style={{ fontSize: isMobile ? 20 : 22, fontWeight:900, color:"#fff", marginBottom:3 }}>Sent Emails</div>
                      <div style={{ fontSize:12, color:"#6b7280" }}>
                        {sentEmails.length > 0 ? `${sentEmails.length} delivered from your Gmail` : "No emails sent yet"}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => setShowSent(false)}
                    style={{ width:38, height:38, borderRadius:12, background:"rgba(255,255,255,0.07)",
                      border:"1px solid rgba(255,255,255,0.1)", color:"#6b7280",
                      cursor:"pointer", fontSize:18, display:"flex", alignItems:"center", justifyContent:"center" }}
                    onMouseEnter={e => { e.currentTarget.style.background="rgba(34,197,94,0.15)"; e.currentTarget.style.color="#4ade80"; }}
                    onMouseLeave={e => { e.currentTarget.style.background="rgba(255,255,255,0.07)"; e.currentTarget.style.color="#6b7280"; }}>✕</button>
                </div>
              </div>

              {/* List */}
              <div style={{ flex:1, overflowY:"auto", padding:"16px",
                scrollbarWidth:"thin", scrollbarColor:"rgba(34,197,94,0.2) transparent" }}>
                {sentEmails.length === 0 ? (
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"center",
                    justifyContent:"center", height:"100%", gap:20, padding:"0 28px", textAlign:"center" }}>
                    <div style={{ fontSize:72, opacity:0.1 }}>📤</div>
                    <div>
                      <div style={{ fontSize:18, fontWeight:800, color:"#4b5563", marginBottom:8 }}>No sent emails yet</div>
                      <div style={{ fontSize:13, color:"#374151", lineHeight:1.7 }}>
                        Ask Santharion AI to write an email,<br/>then send it from the compose window.
                      </div>
                    </div>
                    <div style={{ padding:"14px 20px", borderRadius:16, width:"100%",
                      background:"rgba(34,197,94,0.06)", border:"1px solid rgba(34,197,94,0.12)" }}>
                      <div style={{ fontSize:13, color:"#4ade80", fontWeight:700, marginBottom:4 }}>📧 How to send</div>
                      <div style={{ fontSize:12, color:"#6b7280" }}>Type a request · pick a template · click Send Now</div>
                    </div>
                  </div>
                ) : sentEmails.map((email, i) => (
                  <SentEmailItem key={email.id || i} email={email}/>
                ))}
              </div>

              <div style={{ padding:"14px 20px", flexShrink:0,
                borderTop:"1px solid rgba(255,255,255,0.06)",
                background:"rgba(0,0,0,0.4)",
                display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                <span style={{ fontSize:16 }}>✅</span>
                <span style={{ fontSize:12, color:"#4b5563" }}>
                  Sent via <strong style={{ color:"#4ade80" }}>your Gmail</strong> — recipients see you as sender
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* ── SCHEDULED EMAILS PANEL ── */}
        {showScheduled && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            style={{ position:"fixed", inset:0, zIndex:10000, background:"rgba(0,0,0,0.7)",
              backdropFilter:"blur(8px)", display:"flex",
              alignItems: isMobile ? "flex-end" : "flex-start",
              justifyContent:"flex-start" }}
            onClick={() => setShowScheduled(false)}>
            <motion.div
              initial={{ x: isMobile ? 0 : -420, y: isMobile ? 60 : 0 }}
              animate={{ x:0, y:0 }}
              exit={{ x: isMobile ? 0 : -420, y: isMobile ? 60 : 0 }}
              transition={{ type:"spring", stiffness:320, damping:32 }}
              onClick={e => e.stopPropagation()}
              style={{
                width: isMobile ? "100%" : 460,
                height: isMobile ? "85vh" : "100vh",
                borderRadius: isMobile ? "28px 28px 0 0" : 0,
                background:"linear-gradient(180deg,#120720 0%,#090315 100%)",
                borderRight: isMobile ? "none" : "2px solid rgba(124,58,237,0.15)",
                display:"flex", flexDirection:"column", overflow:"hidden",
                boxShadow: isMobile ? "0 -24px 80px rgba(0,0,0,0.7)" : "24px 0 80px rgba(0,0,0,0.6)"
              }}>

              {isMobile && <div style={{ width:40, height:4, borderRadius:999,
                background:"rgba(255,255,255,0.15)", margin:"12px auto 0", flexShrink:0 }}/>}
              {/* Header */}
              <div style={{ padding: isMobile ? "12px 16px 10px" : "16px 20px 14px", flexShrink:0,
                background:"linear-gradient(135deg,rgba(124,58,237,0.16),rgba(139,92,246,0.08))",
                borderBottom:"1px solid rgba(124,58,237,0.12)" }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                    <div style={{ width:42, height:42, borderRadius:13, flexShrink:0,
                      background:"linear-gradient(135deg,#7c3aed,#a855f7)",
                      boxShadow:"0 6px 20px rgba(124,58,237,0.4)",
                      display:"flex", alignItems:"center", justifyContent:"center", fontSize:22 }}>📅</div>
                    <div>
                      <div style={{ fontSize: isMobile ? 20 : 22, fontWeight:900, color:"#fff", marginBottom:3 }}>Scheduled</div>
                      <div style={{ fontSize:12, color:"#9ca3af" }}>
                        {scheduledEmails.filter(e=>!e.sent&&!e.failed).length > 0
                          ? `${scheduledEmails.filter(e=>!e.sent&&!e.failed).length} queued · auto-sends on time`
                          : "No pending emails"}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => setShowScheduled(false)}
                    style={{ width:38, height:38, borderRadius:12, background:"rgba(255,255,255,0.07)",
                      border:"1px solid rgba(255,255,255,0.1)", color:"#6b7280",
                      cursor:"pointer", fontSize:18, display:"flex", alignItems:"center", justifyContent:"center" }}
                    onMouseEnter={e => { e.currentTarget.style.background="rgba(124,58,237,0.2)"; e.currentTarget.style.color="#c4b5fd"; }}
                    onMouseLeave={e => { e.currentTarget.style.background="rgba(255,255,255,0.07)"; e.currentTarget.style.color="#6b7280"; }}>✕</button>
                </div>
              </div>

              {/* List */}
              <div style={{ flex:1, overflowY:"auto", padding:"16px",
                scrollbarWidth:"thin", scrollbarColor:"rgba(124,58,237,0.2) transparent" }}>
                {scheduledEmails.length === 0 ? (
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"center",
                    justifyContent:"center", height:"100%", gap:20, padding:"0 28px", textAlign:"center" }}>
                    <div style={{ fontSize:72, opacity:0.1 }}>📅</div>
                    <div>
                      <div style={{ fontSize:18, fontWeight:800, color:"#4b5563", marginBottom:8 }}>No scheduled emails</div>
                      <div style={{ fontSize:13, color:"#374151", lineHeight:1.7 }}>
                        Schedule emails to send at the<br/>perfect time automatically.
                      </div>
                    </div>
                    <div style={{ padding:"14px 20px", borderRadius:16, width:"100%",
                      background:"rgba(124,58,237,0.08)", border:"1px solid rgba(124,58,237,0.18)" }}>
                      <div style={{ fontSize:13, color:"#a78bfa", fontWeight:700, marginBottom:4 }}>📅 How to schedule</div>
                      <div style={{ fontSize:12, color:"#6b7280" }}>Compose an email → click <strong style={{ color:"#c4b5fd" }}>📅 Schedule</strong> button</div>
                    </div>
                  </div>
                ) : scheduledEmails.map((email, i) => {
                  const sendDate = new Date(email.sendAt);
                  const isSent   = email.sent && !email.failed;
                  const isFailed = email.failed;
                  const isPending = !email.sent && !email.failed;
                  return (
                    <motion.div key={email.id || i}
                      initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }}
                      transition={{ delay: i * 0.04 }}
                      style={{ marginBottom:8, padding:"12px 14px", borderRadius:13,
                        background: isFailed ? "rgba(239,68,68,0.06)" : isSent ? "rgba(34,197,94,0.05)" : "rgba(124,58,237,0.07)",
                        border: `1px solid ${isFailed ? "rgba(239,68,68,0.25)" : isSent ? "rgba(34,197,94,0.2)" : "rgba(124,58,237,0.2)"}` }}>

                      {/* Row 1: status + cancel */}
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                        <span style={{ fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:999,
                          background: isFailed ? "rgba(239,68,68,0.15)" : isSent ? "rgba(34,197,94,0.15)" : "rgba(124,58,237,0.18)",
                          color: isFailed ? "#f87171" : isSent ? "#4ade80" : "#c4b5fd" }}>
                          {isFailed ? "❌ Failed" : isSent ? "✅ Sent" : "⏳ Queued"}
                        </span>
                        {isPending && (
                          <button onClick={() => {
                            setScheduledEmails(prev => prev.filter(e => e.id !== email.id));
                            if (email.serverManaged) {
                              fetch(`/api/email/scheduled/${email.id}`, { method:"DELETE" }).catch(()=>{});
                            }
                          }}
                            style={{ padding:"2px 8px", borderRadius:6, background:"none",
                              border:"1px solid rgba(239,68,68,0.2)", color:"#f87171",
                              cursor:"pointer", fontSize:10, fontWeight:700, fontFamily:"inherit" }}>
                            ✕ Cancel
                          </button>
                        )}
                      </div>

                      {/* Row 2: subject */}
                      <div style={{ fontSize:13, fontWeight:700, color:"#e5e7eb", marginBottom:3,
                        overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {email.subject}
                      </div>

                      {/* Row 3: to + time on same line */}
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
                        <div style={{ fontSize:11, color:"#6b7280", overflow:"hidden",
                          textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1 }}>
                          → <span style={{ color:"#a78bfa" }}>{email.to}</span>
                        </div>
                        <div style={{ fontSize:11, fontWeight:600, flexShrink:0,
                          color: isSent ? "#4ade80" : isFailed ? "#f87171" : "#9ca3af" }}>
                          {sendDate.toLocaleString("en-IN",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Footer */}
              <div style={{ padding:"14px 20px", flexShrink:0,
                borderTop:"1px solid rgba(255,255,255,0.06)",
                background:"rgba(0,0,0,0.4)", textAlign:"center" }}>
                <span style={{ fontSize:12, color:"#6b7280" }}>
                  ⚠️ Keep this tab open — emails send automatically when due
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}